(function (window, $) {
  'use strict';

  if (!$) return;

  var NP = window.TPB = window.TPB || {};
  NP.NotebookPersonalisation = NP.NotebookPersonalisation || {};

  var STEPS = ['cover', 'insert', 'addons', 'monogram'];
  // Mirrors `min_aspect_ratio` in sections/main-product.liquid.
  var NP_MIN_ASPECT_RATIO = 0.6;
  // Horizontal travel before a drag counts as an insert viewer swipe.
  var NP_SWIPE_MIN_DISTANCE = 40;
  // A phone's short edge stays well under this even in landscape; tablets do not.
  var NP_PHONE_SHORT_EDGE = 600;
  var NP_PARAMS = [
    'np_insert',
    'np_charms',
    'np_accessories',
    'np_mono',
    'np_mono_text',
    'np_mono_font',
    'np_step'
  ];

  function parseIdList(raw) {
    if (!raw) return [];
    return String(raw)
      .split(',')
      .map(function (part) {
        return Number(String(part).trim());
      })
      .filter(function (id) {
        return id && !isNaN(id);
      });
  }

  // Renders one `money_format` placeholder; the token name carries the
  // separator and decimal rules, so the same amount is re-derived per token.
  function moneyToken(cents, token) {
    var amount = (Number(cents) || 0) / 100;
    var value = token.indexOf('no_decimals') > -1
      ? Math.round(amount).toLocaleString('en-US')
      : amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    if (token.indexOf('apostrophe_separator') > -1) return value.replace(/,/g, "'");

    if (token.indexOf('comma_separator') > -1 || token.indexOf('space_separator') > -1) {
      // "1,234.56" -> "1.234,56", via a placeholder so the swap is not reentrant.
      var swapped = value.replace(/,/g, '\u0000').replace(/\./g, ',').replace(/\u0000/g, '.');
      if (token.indexOf('space_separator') > -1) return swapped.replace(/\./g, ' ');
      return swapped;
    }

    return value;
  }

  // Formats against the shop's own money format so the symbol, separators and
  // placement follow the active currency rather than a hardcoded locale.
  function money(cents, config, withCurrencyCode) {
    var format = withCurrencyCode && config.showCurrencyCode
      ? config.moneyWithCurrencyFormat || config.moneyFormat
      : config.moneyFormat;

    if (!format) {
      var amount = (Number(cents) || 0) / 100;
      return (config.currencySymbol || '') +
        amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }

    return String(format).replace(/\{\{\s*(\w+)\s*\}\}/g, function (match, token) {
      return moneyToken(cents, token);
    });
  }

  // Checkout allocates Bundle and Early Bird separately off the list price, each
  // amount rounded to the nearest 50, rather than compounding the percentages.
  // The configurator has to derive its total the same way or the price on the
  // page will not match the cart.
  var DISCOUNT_ROUNDING_CENTS = 5000;

  function discountAmountCents(cents, pct) {
    var amount = Number(cents) || 0;
    var percent = Number(pct) || 0;
    if (percent <= 0) return 0;
    return (
      Math.round((amount * percent) / 100 / DISCOUNT_ROUNDING_CENTS) *
      DISCOUNT_ROUNDING_CENTS
    );
  }

  function discountedCents(cents, pct) {
    var amount = Number(cents) || 0;
    return Math.max(0, amount - discountAmountCents(amount, pct));
  }

  // Theme labels may be singular or plural; match grammar to the remaining count.
  function charmCountLabel(count, label) {
    var base = String(label || '').trim();
    if (!base) return base;
    if (count === 1) {
      if (/s$/i.test(base) && !/ss$/i.test(base)) {
        return base.replace(/s$/i, '');
      }
      return base;
    }
    if (/s$/i.test(base)) return base;
    return base + 's';
  }

  function isSerifFontStyle(fontText) {
    return String(fontText || '').trim().toLowerCase() === 'serif';
  }

  // An invalid offset silently resolves to `auto`, which would drop the layer at
  // its static position, so only usable lengths are accepted.
  function cssLength(token) {
    var value = String(token == null ? '' : token).trim();
    if (!value) return null;
    if (/^-?\d+(\.\d+)?$/.test(value)) return value + '%';
    if (/^-?\d+(\.\d+)?(%|px|em|rem|vw|vh)$/.test(value)) return value;
    if (/^calc\(.+\)$/.test(value)) return value;
    return null;
  }

  // Metaobject position fields arrive as a string, a list of "top,left" strings,
  // or a flat list of tokens, and may include descriptive labels such as
  // "bottom right" alongside the coordinates, so the two are separated here.
  function positionPairs(raw) {
    var offsets = [];
    var labels = [];

    function collect(value) {
      if (value == null) return;
      if (Array.isArray(value)) {
        value.forEach(collect);
        return;
      }
      if (typeof value === 'object') {
        if (value.top != null) collect(value.top);
        if (value.left != null) collect(value.left);
        return;
      }
      String(value)
        .split(/[\n|;,]/)
        .forEach(function (token) {
          var trimmed = token.trim();
          if (!trimmed) return;
          var length = cssLength(trimmed);
          if (length) {
            offsets.push(length);
          } else {
            labels.push(trimmed);
          }
        });
    }

    collect(raw);

    var pairs = [];
    for (var i = 0; i + 1 < offsets.length; i += 2) {
      pairs.push({
        top: offsets[i],
        left: offsets[i + 1],
        label: labels[pairs.length] || null
      });
    }
    return pairs;
  }

  function parsePositions(raw, totalNeeded) {
    var list = positionPairs(raw);
    while (list.length < totalNeeded) {
      list.push({ top: null, left: null });
    }
    return list.map(function (pos, index) {
      return {
        top: pos.top || '8%',
        left: pos.left || (12 + index * 10) + '%'
      };
    });
  }

  function parseMonoPos(raw, fallback) {
    var pos = positionPairs(raw)[0];
    if (!pos) return fallback;
    if (!fallback) fallback = {};
    return {
      top: pos.top || fallback.top,
      left: pos.left || fallback.left,
      label: pos.label || fallback.label || null
    };
  }

  // Responsive metaobject values use:
  // desktopTop,desktopLeft;mobileTop,mobileLeft
  // A missing mobile half reuses desktop, while values without `;` preserve
  // the legacy behavior on both viewports.
  function parseResponsivePosition(raw, mobile, fallback) {
    if (raw == null) return fallback;
    var value = String(raw).trim();
    var separator = value.indexOf(';');
    if (separator === -1) return parseMonoPos(raw, fallback);

    var desktop = parseMonoPos(value.slice(0, separator), fallback);
    if (!mobile) return desktop;
    return parseMonoPos(value.slice(separator + 1), desktop);
  }

  // Monogram fields may still be a list with a leftover title row. Use the
  // first entry that contains coordinates; ignore titles.
  function monogramPositionValue(raw) {
    if (raw == null) return raw;
    if (!Array.isArray(raw)) return raw;

    var axis = null;
    raw.forEach(function (entry) {
      if (axis != null) return;
      if (positionPairs(entry).length) axis = entry;
    });
    if (axis != null) return axis;
    return raw.length ? raw[raw.length - 1] : raw;
  }

  function parseResponsivePositions(raw, totalNeeded, mobile) {
    var hasResponsiveValues =
      Array.isArray(raw)
        ? raw.some(function (value) {
            return String(value == null ? '' : value).indexOf(';') > -1;
          })
        : String(raw == null ? '' : raw).indexOf(';') > -1;

    // Legacy values without a responsive separator may use comma or pipe as
    // generic coordinate separators, so leave them with the original parser.
    if (!hasResponsiveValues) return parsePositions(raw, totalNeeded);

    var entries = Array.isArray(raw) ? raw : [raw];
    var list = entries.map(function (value, index) {
      return parseResponsivePosition(value, mobile, {
        top: '8%',
        left: 12 + index * 10 + '%'
      });
    });
    while (list.length < totalNeeded) {
      var index = list.length;
      list.push({ top: '8%', left: 12 + index * 10 + '%' });
    }
    return list.slice(0, totalNeeded);
  }

  var App = {
    $root: null,
    config: null,
    state: null,

    init: function ($root) {
      this.$root = $root;
      var raw = $root.find('[data-np-config]').text();
      try {
        this.config = JSON.parse(raw);
      } catch (e) {
        console.error('[NP] Invalid config JSON', e);
        return;
      }
      this.state = this.defaultState();
      NP.NotebookPersonalisation.current = this;
      document.documentElement.classList.remove('jsc-np-insert-viewer-open');
      document.body.classList.remove('jsc-np-insert-viewer-open');
      this.restoreFromParams();
      this.syncCoverToCurrentProduct();
      this.ensureDefaults();
      this.injectPreviewSlide();
      this.filterNpGalleryByViewport();
      this.bindGlobal();
      this.renderAll();
      this.goToStep(this.state.currentStep, true);
      this.selectPreviewFirst();
      this.observeStickyOffsets();
      this.bindStickyStackGlue();
      this.persist();
    },

    defaultState: function () {
      return {
        currentStep: 0,
        maxStepReached: 0,
        coverId: null,
        insertIndex: null,
        noAddOns: false,
        addCharms: false,
        tpbCharmIds: [],
        regularCharmIds: [],
        charmOrderIds: [],
        accessoryIds: [],
        noMonogram: false,
        wantMonogram: false,
        monogramText: '',
        monogramFontId: null,
        monogramTerms: false
      };
    },

    isProductAvailable: function (product) {
      return !!(product && product.available !== false && product.variantId);
    },

    getFirstAvailableCover: function () {
      var covers = this.config.covers || [];
      for (var i = 0; i < covers.length; i++) {
        if (this.isProductAvailable(covers[i])) return covers[i];
      }
      return null;
    },

    getFirstAvailableInsert: function () {
      var inserts = this.config.inserts || [];
      for (var i = 0; i < inserts.length; i++) {
        if (inserts[i] && this.isProductAvailable(inserts[i].product)) return inserts[i];
      }
      return null;
    },

    hasAnyAvailableInsert: function () {
      return !!this.getFirstAvailableInsert();
    },

    isCurrentCoverAvailable: function () {
      return this.isProductAvailable(this.getCover());
    },

    isMonogramProductAvailable: function () {
      return this.isProductAvailable(this.config.monogram && this.config.monogram.product);
    },

    // Mini Charms require a configured, in-stock parent SKU for pricing.
    isCharmParentAvailable: function () {
      var parent = this.charmParent();
      if (!parent) return false;
      return this.isProductAvailable(parent);
    },

    hasSelectableCharms: function () {
      return (
        this.canFulfillCharmGroup(
          this.config.tpbCharms.products,
          this.config.tpbCharms.limit
        ) &&
        this.canFulfillCharmGroup(
          this.config.regularCharms.products,
          this.config.regularCharms.limit
        )
      );
    },

    // A group is fulfillable when in-stock charms meet (or exceed) its metaobject limit.
    canFulfillCharmGroup: function (products, limit) {
      var needed = Number(limit) || 0;
      if (needed <= 0) return true;
      return this.availableCount(products) >= needed;
    },

    // Feature is on only when parent set is buyable AND every charm group can meet its limit.
    isCharmsFeatureAvailable: function () {
      return this.isCharmParentAvailable() && this.hasSelectableCharms();
    },

    hasAnyAvailableAddon: function () {
      var self = this;
      var found = false;
      if (this.isCharmParentAvailable()) {
        (this.config.tpbCharms.products || []).forEach(function (p) {
          if (self.isProductAvailable(p)) found = true;
        });
        (this.config.regularCharms.products || []).forEach(function (p) {
          if (self.isProductAvailable(p)) found = true;
        });
      }
      (this.config.accessories || []).forEach(function (p) {
        if (self.isProductAvailable(p)) found = true;
      });
      return found;
    },

    lowestAvailablePrice: function (products) {
      var self = this;
      var lowest = null;
      (products || []).forEach(function (p) {
        if (!self.isProductAvailable(p)) return;
        var price = Number(p.price) || 0;
        if (lowest == null || price < lowest) lowest = price;
      });
      return lowest;
    },

    getLowestAvailableCharmPrice: function () {
      return this.lowestAvailablePrice(
        [].concat(this.config.tpbCharms.products || []).concat(this.config.regularCharms.products || [])
      );
    },

    availableCount: function (products) {
      var self = this;
      var count = 0;
      (products || []).forEach(function (p) {
        if (self.isProductAvailable(p)) count += 1;
      });
      return count;
    },

    // Metaobject limit is the required pick count. Soft-cap only as a safety net;
    // under-stocked groups are treated as sold out via isCharmsFeatureAvailable().
    charmRequirements: function () {
      var tpbLimit = Number(this.config.tpbCharms.limit) || 0;
      var regularLimit = Number(this.config.regularCharms.limit) || 0;
      return {
        tpb: Math.min(tpbLimit, this.availableCount(this.config.tpbCharms.products)),
        regular: Math.min(regularLimit, this.availableCount(this.config.regularCharms.products))
      };
    },

    // Accessories are full-bleed overlays and do not consume charm position slots.
    charmPositions: function () {
      var tpbLimit = Number(this.config.tpbCharms.limit) || 0;
      var regularLimit = Number(this.config.regularCharms.limit) || 0;
      return parseResponsivePositions(
        this.config.charmsPosition,
        tpbLimit + regularLimit,
        this.isMobile()
      );
    },

    orderedCharmIds: function () {
      var selectedIds = []
        .concat(this.state.tpbCharmIds || [])
        .concat(this.state.regularCharmIds || []);
      var orderedIds = [];
      (this.state.charmOrderIds || []).concat(selectedIds).forEach(function (id) {
        if (selectedIds.indexOf(id) > -1 && orderedIds.indexOf(id) === -1) {
          orderedIds.push(id);
        }
      });
      return orderedIds;
    },

    removeCharmFromOrder: function (id) {
      this.state.charmOrderIds = this.orderedCharmIds().filter(function (orderedId) {
        return orderedId !== id;
      });
    },

    appendCharmToOrder: function (id) {
      this.removeCharmFromOrder(id);
      this.state.charmOrderIds.push(id);
    },

    toggleCharmSelection: function (idsKey, id, limit) {
      var ids = this.state[idsKey] || [];
      var idx = ids.indexOf(id);
      if (idx > -1) {
        ids.splice(idx, 1);
        this.removeCharmFromOrder(id);
      } else if (ids.length < limit) {
        ids.push(id);
        this.appendCharmToOrder(id);
      } else {
        var replaced = ids.shift();
        if (replaced != null) this.removeCharmFromOrder(replaced);
        ids.push(id);
        this.appendCharmToOrder(id);
      }
      this.state[idsKey] = ids;
    },

    setAddonsCollapsed: function (collapsed) {
      var $block = this.$root.find('[data-np-addons-block]');
      var $toggle = this.$root.find('[data-np-addons-toggle]');
      $block.toggleClass('is-collapsed', !!collapsed);
      $toggle.attr('aria-expanded', collapsed ? 'false' : 'true');
      $toggle.find('.visually-hidden').text(collapsed ? 'Show add-ons' : 'Hide add-ons');
    },

    productListIndex: function (products, id) {
      var list = products || [];
      for (var i = 0; i < list.length; i++) {
        if (Number(list[i].id) === Number(id)) return i;
      }
      return -1;
    },

    sanitizeUnavailableSelections: function () {
      // Cover is PDP-scoped: never auto-switch to another cover product.
      if (this.config.currentProductId) {
        this.state.coverId = this.config.currentProductId;
      }

      // Only sanitize a chosen insert; leave null so the placeholder stays until the customer picks.
      if (this.state.insertIndex != null) {
        var insert = this.getInsert();
        if (!insert || !this.isProductAvailable(insert.product)) {
          this.state.insertIndex = null;
        }
      }

      var self = this;
      this.state.tpbCharmIds = (this.state.tpbCharmIds || []).filter(function (id) {
        var product = null;
        (self.config.tpbCharms.products || []).forEach(function (p) {
          if (p.id === id) product = p;
        });
        return self.isProductAvailable(product);
      });
      this.state.regularCharmIds = (this.state.regularCharmIds || []).filter(function (id) {
        var product = null;
        (self.config.regularCharms.products || []).forEach(function (p) {
          if (p.id === id) product = p;
        });
        return self.isProductAvailable(product);
      });
      this.state.charmOrderIds = this.orderedCharmIds();
      this.state.accessoryIds = (this.state.accessoryIds || []).filter(function (id) {
        var product = null;
        (self.config.accessories || []).forEach(function (p) {
          if (p.id === id) product = p;
        });
        return self.isProductAvailable(product);
      });

      if (!this.hasAnyAvailableAddon()) {
        this.state.noAddOns = true;
        this.state.addCharms = false;
        this.state.tpbCharmIds = [];
        this.state.regularCharmIds = [];
        this.state.charmOrderIds = [];
        this.state.accessoryIds = [];
      }

      if (!this.isCharmsFeatureAvailable()) {
        this.state.addCharms = false;
        this.state.tpbCharmIds = [];
        this.state.regularCharmIds = [];
        this.state.charmOrderIds = [];
      }

      if (!this.isMonogramProductAvailable()) {
        this.state.wantMonogram = false;
        this.state.noMonogram = true;
        this.state.monogramText = '';
        this.state.monogramFontId = null;
        this.state.monogramTerms = false;
      }
    },

    ensureDefaults: function () {
      var c = this.config;
      // Always pin to the current PDP cover product.
      if (c.currentProductId) {
        this.state.coverId = c.currentProductId;
      } else if (!this.state.coverId && c.covers && c.covers.length) {
        this.state.coverId = c.covers[0].id;
      }

      this.sanitizeUnavailableSelections();
      if (this.state.wantMonogram && c.monogram && c.monogram.fonts && c.monogram.fonts.length && !this.state.monogramFontId) {
        this.state.monogramFontId = c.monogram.fonts[0].id;
      }
    },

    syncCoverToCurrentProduct: function () {
      if (this.config.currentProductId) {
        this.state.coverId = this.config.currentProductId;
      }
    },

    buildParamMap: function () {
      var params = {};
      var insert = this.getInsert();
      if (
        (this.state.maxStepReached >= 1 || this.state.currentStep >= 1) &&
        insert &&
        insert.product &&
        insert.product.id
      ) {
        params.np_insert = String(insert.product.id);
      }

      if (!this.state.noAddOns) {
        var charmIds = this.orderedCharmIds().filter(Boolean);
        if (charmIds.length) {
          params.np_charms = charmIds.join(',');
        }
        var accessoryIds = (this.state.accessoryIds || []).filter(Boolean);
        if (accessoryIds.length) {
          params.np_accessories = accessoryIds.join(',');
        }
      }

      if (this.state.wantMonogram && !this.state.noMonogram) {
        params.np_mono = '1';
        if (this.state.monogramText) {
          params.np_mono_text = String(this.state.monogramText);
        }
        if (this.state.monogramFontId != null) {
          params.np_mono_font = String(this.state.monogramFontId);
        }
      }

      if (this.state.currentStep > 0) {
        params.np_step = String(this.state.currentStep);
      }

      return params;
    },

    applyParamsToSearch: function (searchParams, npParams) {
      NP_PARAMS.forEach(function (key) {
        searchParams.delete(key);
      });
      Object.keys(npParams || {}).forEach(function (key) {
        if (npParams[key] != null && npParams[key] !== '') {
          searchParams.set(key, npParams[key]);
        }
      });
      return searchParams;
    },

    urlWithParams: function (url) {
      var target;
      try {
        target = new URL(url, window.location.origin);
      } catch (e) {
        return url;
      }
      this.applyParamsToSearch(target.searchParams, this.buildParamMap());
      return target.pathname + target.search + target.hash;
    },

    persist: function () {
      var prevMax = this.state.maxStepReached;
      this.clampMaxStepReached();
      if (this.state.currentStep > this.state.maxStepReached) {
        this.goToStep(this.state.maxStepReached, true);
      } else if (this.state.maxStepReached !== prevMax) {
        this.renderStepper();
      }
      try {
        var url = new URL(window.location.href);
        this.applyParamsToSearch(url.searchParams, this.buildParamMap());
        var next = url.pathname + url.search + url.hash;
        if (next !== window.location.pathname + window.location.search + window.location.hash) {
          window.history.replaceState(window.history.state, '', next);
        }
      } catch (e) {}
    },

    clearParams: function () {
      try {
        var url = new URL(window.location.href);
        NP_PARAMS.forEach(function (key) {
          url.searchParams.delete(key);
        });
        var next = url.pathname + url.search + url.hash;
        window.history.replaceState(window.history.state, '', next);
      } catch (e) {}
    },

    restoreFromParams: function () {
      try {
        var params = new URLSearchParams(window.location.search);
        var hasAny = NP_PARAMS.some(function (key) {
          return params.has(key);
        });
        if (!hasAny) return;

        var insertId = Number(params.get('np_insert'));
        var insertApplied = false;
        if (insertId && !isNaN(insertId)) {
          var matchedInsert = null;
          (this.config.inserts || []).forEach(function (ins) {
            if (ins && ins.product && Number(ins.product.id) === insertId) {
              matchedInsert = ins;
            }
          });
          if (matchedInsert) {
            this.state.insertIndex = matchedInsert.index;
            insertApplied = true;
          }
        }

        var charmIds = parseIdList(params.get('np_charms'));
        var accessoryIds = parseIdList(params.get('np_accessories'));
        var tpbIds = [];
        var regularIds = [];
        var orderedCharmIds = [];
        var self = this;

        charmIds.forEach(function (id) {
          var isTpb = (self.config.tpbCharms.products || []).some(function (p) {
            return Number(p.id) === id;
          });
          var isRegular = (self.config.regularCharms.products || []).some(function (p) {
            return Number(p.id) === id;
          });
          if (isTpb) {
            tpbIds.push(id);
            orderedCharmIds.push(id);
          } else if (isRegular) {
            regularIds.push(id);
            orderedCharmIds.push(id);
          }
        });

        var validAccessories = accessoryIds.filter(function (id) {
          return (self.config.accessories || []).some(function (p) {
            return Number(p.id) === id;
          });
        });

        this.state.tpbCharmIds = tpbIds;
        this.state.regularCharmIds = regularIds;
        this.state.charmOrderIds = orderedCharmIds;
        this.state.accessoryIds = validAccessories;

        if (tpbIds.length || regularIds.length || validAccessories.length) {
          this.state.noAddOns = false;
          this.state.addCharms = !!(tpbIds.length || regularIds.length);
        }

        var monoApplied = false;
        if (params.get('np_mono') === '1') {
          monoApplied = true;
          this.state.wantMonogram = true;
          this.state.noMonogram = false;
          this.state.monogramTerms = false;
          var fontId = Number(params.get('np_mono_font'));
          if (fontId && !isNaN(fontId)) {
            var fontOk = (this.config.monogram && this.config.monogram.fonts || []).some(function (f) {
              return Number(f.id) === fontId;
            });
            if (fontOk) {
              this.state.monogramFontId = fontId;
            }
          }
          if (params.get('np_mono_text') != null) {
            this.state.monogramText = this.sanitizeMonogramText(params.get('np_mono_text'));
          }
        }

        var maxReached = this.state.maxStepReached || 0;
        var step = Number(params.get('np_step'));
        if (!isNaN(step) && step >= 0) {
          this.state.currentStep = Math.min(step, STEPS.length - 1);
          maxReached = Math.max(maxReached, this.state.currentStep);
        }
        // A restored selection means that step was completed, so record the step after it.
        var lastStep = STEPS.length - 1;
        if (insertApplied) maxReached = Math.max(maxReached, Math.min(2, lastStep));

        var charmsParam = params.get('np_charms');
        var accessoriesParam = params.get('np_accessories');
        var hasAddonParams = !!(
          (charmsParam && String(charmsParam).trim()) ||
          (accessoriesParam && String(accessoriesParam).trim())
        );
        // Raw param presence unlocks Add-ons (clickable) even if IDs don't match this cover.
        if (hasAddonParams) maxReached = Math.max(maxReached, Math.min(2, lastStep));
        // Matched selections also mark Add-ons done (one step past).
        if (tpbIds.length || regularIds.length || validAccessories.length) {
          maxReached = Math.max(maxReached, Math.min(3, lastStep));
        }
        if (monoApplied) maxReached = Math.max(maxReached, lastStep);
        this.state.maxStepReached = Math.min(maxReached, lastStep);
      } catch (e) {}
    },

    getCoverIndex: function () {
      var id = this.state.coverId;
      var idx = -1;
      (this.config.covers || []).forEach(function (c, i) {
        if (c.id === id) idx = i;
      });
      return idx;
    },

    getCover: function () {
      var i = this.getCoverIndex();
      return i > -1 ? this.config.covers[i] : (this.config.covers || [])[0];
    },

    getMonogramFont: function () {
      var font = null;
      var id = this.state.monogramFontId;
      var fonts = (this.config.monogram && this.config.monogram.fonts) || [];
      fonts.forEach(function (f) {
        if (Number(f.id) === Number(id)) font = f;
      });
      return font || fonts[0] || null;
    },

    monogramPosition: function () {
      var font = this.getMonogramFont();
      var mono = this.config.monogram || {};
      var mobile = this.isMobile();
      var pos = parseResponsivePosition(
        monogramPositionValue(mono.position1),
        mobile,
        { top: '78%', left: '50%' }
      );
      if (font && Number(font.id) === 2) {
        pos = parseResponsivePosition(
          monogramPositionValue(mono.position2),
          mobile,
          pos
        );
      }
      return pos;
    },

    getMonogramCharLimit: function () {
      var font = this.getMonogramFont();
      var limit = font && font.charLimit != null ? Number(font.charLimit) : Number(this.config.monogram && this.config.monogram.maxChars);
      if (!limit || isNaN(limit) || limit < 1) limit = 2;
      return limit;
    },

    sanitizeMonogramText: function (text) {
      var value = String(text || '');
      var font = this.getMonogramFont();
      if (!font || font.allowSpecialChars !== true) {
        value = value.replace(/[^a-zA-Z0-9]/g, '');
      }
      return value.slice(0, this.getMonogramCharLimit());
    },

    getInsert: function () {
      if (this.state.insertIndex == null) return null;
      var idx = this.state.insertIndex;
      var found = null;
      (this.config.inserts || []).forEach(function (ins) {
        if (ins.index === idx) found = ins;
      });
      return found;
    },

    // Detail panel / preview fallback when the customer has not chosen yet.
    getInsertForDetail: function () {
      return this.getInsert() || this.getFirstAvailableInsert() || (this.config.inserts || [])[0] || null;
    },

    getInsertListIndex: function () {
      if (this.state.insertIndex == null) return -1;
      var idx = this.state.insertIndex;
      var listIdx = -1;
      (this.config.inserts || []).forEach(function (ins, i) {
        if (ins.index === idx) listIdx = i;
      });
      return listIdx;
    },

    // Bundle tiers: charms group = 1 add-on (any qty / estimates), each accessory = 1.
    addOnCount: function (estimatedCharmSlots) {
      if (this.state.noAddOns) return 0;
      var count = (this.state.accessoryIds || []).length;
      var charmSelected =
        (this.state.tpbCharmIds || []).length + (this.state.regularCharmIds || []).length;
      var estimating = (estimatedCharmSlots || 0) > 0;
      if (this.state.addCharms && (charmSelected > 0 || estimating)) {
        count += 1;
      }
      return count;
    },

    // Every charm slot the limit asks for is priced, so unfilled slots are
    // estimated at their group's lowest available charm.
    charmParent: function () {
      var parent = this.config && this.config.charmParent;
      if (!parent || !parent.variantId) return null;
      return parent;
    },

    charmTotals: function () {
      var self = this;
      var required = this.charmRequirements();
      var estimate = this.state.addCharms && !this.state.noAddOns;
      var parent = this.charmParent();

      // Set price lives on the merchant-chosen parent SKU. Unfilled slots are
      // still counted as estimates so bundle tiers can preview before the
      // customer finishes picking.
      if (parent && estimate) {
        var selected =
          (this.state.tpbCharmIds || []).length + (this.state.regularCharmIds || []).length;
        var requiredCount = required.tpb + required.regular;
        return {
          total: parent.price || 0,
          estimatedCount: Math.max(0, requiredCount - selected)
        };
      }

      var total = 0;
      var estimatedCount = 0;

      function addGroup(products, selectedIds, requiredCount) {
        var selected = 0;
        (products || []).forEach(function (p) {
          if (selectedIds.indexOf(p.id) > -1) {
            total += p.price || 0;
            selected += 1;
          }
        });
        if (!estimate) return;
        var remaining = requiredCount - selected;
        if (remaining <= 0) return;
        var lowest = self.lowestAvailablePrice(products);
        if (lowest == null) return;
        total += lowest * remaining;
        estimatedCount += remaining;
      }

      addGroup(this.config.tpbCharms.products, this.state.tpbCharmIds, required.tpb);
      addGroup(this.config.regularCharms.products, this.state.regularCharmIds, required.regular);

      return { total: total, estimatedCount: estimatedCount };
    },

    pricing: function () {
      var cover = this.getCover();
      var insert = this.getInsert();
      // kind: 'core' (folio+insert) | 'addon' (charms+accessories) | 'monogram'
      var lines = [];
      var estimatedCount = 0;
      if (cover) lines.push({ cents: cover.price || 0, kind: 'core' });
      if (insert && insert.product) {
        lines.push({ cents: insert.product.price || 0, kind: 'core' });
      }

      var self = this;
      var charms = { total: 0, estimatedCount: 0 };
      if (!this.state.noAddOns) {
        charms = this.charmTotals();
        if (charms.total) lines.push({ cents: charms.total, kind: 'addon' });
        estimatedCount = charms.estimatedCount;
        (this.config.accessories || []).forEach(function (p) {
          if (self.state.accessoryIds.indexOf(p.id) > -1) {
            lines.push({ cents: p.price || 0, kind: 'addon' });
          }
        });
      }

      if (this.state.wantMonogram && !this.state.noMonogram && this.isMonogramProductAvailable()) {
        lines.push({ cents: this.config.monogram.product.price || 0, kind: 'monogram' });
      }

      var subtotal = 0;
      lines.forEach(function (line) {
        subtotal += line.cents;
      });

      var count = this.addOnCount(estimatedCount);
      var tiers = (this.config.discounts.bundleTiers || []).slice().sort(function (a, b) {
        return a.minCount - b.minCount;
      });
      var bundlePct = 0;
      tiers.forEach(function (t) {
        if (count >= t.minCount) bundlePct = t.percent;
      });

      var earlyPct = 0;
      if (this.config.discounts.earlyBirdEnabled && this.config.discounts.customerHasEarlyBird) {
        earlyPct = this.config.discounts.earlyBirdPercent || 0;
      }

      // Folio+insert: Bundle and Early Bird, both off the list price.
      // Add-ons (charms+accessories): Bundle and Early Bird, both off the list price.
      // Monogram: full price (no Bundle, no Early Bird).
      var finalPrice = 0;
      lines.forEach(function (line) {
        var cents = line.cents;
        if (line.kind !== 'monogram') {
          cents -= discountAmountCents(line.cents, bundlePct);
          cents -= discountAmountCents(line.cents, earlyPct);
        }
        finalPrice += Math.max(0, cents);
      });

      return {
        subtotal: subtotal,
        final: finalPrice,
        bundlePct: bundlePct,
        earlyPct: earlyPct,
        addOnCount: count
      };
    },

    isMobile: function () {
      return window.innerWidth < 768;
    },

    // The insert viewer is landscape-only on phones, where the viewport is wider
    // than 767px, so width alone would hand those users the desktop artwork.
    isInsertMediaMobile: function () {
      if (this.isMobile()) return true;
      var coarse = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
      if (!coarse) return false;
      var screenSize = window.screen || {};
      var shortEdge = Math.min(
        screenSize.width || window.innerWidth,
        screenSize.height || window.innerHeight
      );
      return shortEdge < NP_PHONE_SHORT_EDGE;
    },

    // Only applies to the JS-injected slide; when the section renders the
    // preview it already sets --aspect-ratio from the cover overlay.
    syncPreviewAspectRatio: function ($preview) {
      var host = $preview.closest('.jsc-np-preview-host')[0];
      if (!host || host.hasAttribute('data-np-ar-from-liquid')) return;
      var img = $preview.find('[data-np-layer="cover"]')[0];
      if (!img) return;

      function apply() {
        if (!img.naturalWidth || !img.naturalHeight) return;
        var ratio = Math.max(img.naturalWidth / img.naturalHeight, NP_MIN_ASPECT_RATIO);
        host.style.setProperty('--aspect-ratio', ratio);
      }

      if (img.complete) {
        apply();
      } else {
        $(img).one('load', apply);
      }
    },

    // The gallery viewer matches the clicked link's href against the thumb
    // data-zoom-url, so both have to follow the selected cover. The section
    // can only emit that thumb when it resolves the overlay server-side, so
    // add it here when the preview slide came from injectPreviewSlide.
    syncPreviewZoomTarget: function (coverIdx, fallbackSrc) {
      var zoomSrc = (this.config.coverOverlaysZoom || [])[coverIdx] || fallbackSrc;
      if (!zoomSrc) return;

      $('[data-np-preview-zoom]').attr('href', zoomSrc);

      var galleryEl = $('media-gallery.jsc-np-media-gallery, .jsc-np-media-gallery').first()[0];
      var modal = galleryEl && galleryEl.galleryModal;
      if (!modal) {
        // The clone is made in the media-gallery constructor, which can run
        // after the first render. Try again once it is defined.
        if (!this.awaitingGalleryModal && window.customElements && window.customElements.whenDefined) {
          this.awaitingGalleryModal = true;
          var self = this;
          window.customElements.whenDefined('media-gallery').then(function () {
            self.awaitingGalleryModal = false;
            self.syncPreviewZoomTarget(coverIdx, fallbackSrc);
          });
        }
        return;
      }

      var $thumbs = $(modal).find('.gallery-viewer__thumbs');
      if (!$thumbs.length) return;

      var $thumb = $thumbs.find('[data-np-preview-thumb]');
      if (!$thumb.length) {
        $thumb = $(
          '<a class="gallery-viewer__thumb" href="#" data-np-preview-thumb>' +
            '<img class="gallery-viewer__thumb-img" alt="">' +
          '</a>'
        ).prependTo($thumbs);

        // Only set when the product had a single image, and it hides the
        // whole thumb strip along with the prev/next arrows.
        $(modal).find('gallery-viewer').removeClass('gallery-viewer--single-image');
        galleryEl.mediaLists = (galleryEl.mediaLists || []).filter(function (list) {
          return !list.length || !list[0].classList.contains('gallery-viewer__thumb');
        });
        galleryEl.mediaLists.push(modal.querySelectorAll('.gallery-viewer__thumb'));
      }

      $thumb
        .attr('data-zoom-url', zoomSrc)
        .attr('data-media-id', 'np-preview-' + this.config.currentProductId)
        .find('img')
        .attr('src', (this.config.coverOverlaysMobile || [])[coverIdx] || zoomSrc);

      // The modal is cloned after the first viewport filter may have run.
      this.filterNpGalleryByViewport();
    },

    // On mobile the NP preview stays first and only product media explicitly
    // tagged "mobile-display" is shown. Desktop uses the normal media set.
    filterNpGalleryByViewport: function () {
      var $gallery = $('media-gallery.jsc-np-media-gallery, .jsc-np-media-gallery').first();
      if (!$gallery.length) return;

      var isMobile = this.isMobile();
      var viewportChanged = this.npGalleryFilterMobile !== isMobile;
      this.npGalleryFilterMobile = isMobile;

      var galleryEl = $gallery[0];
      var $items = $gallery.find('.main-image .slider__item, .thumbnails .slider__item');
      if (galleryEl.galleryModal) {
        $items = $items.add($(galleryEl.galleryModal).find('.gallery-viewer__thumb'));
      }

      $items.each(function () {
        var isPreview = $(this).is('.jsc-np-preview-slide, .jsc-np-preview-thumbnail, [data-np-preview-thumb]');
        var alt = ($(this).attr('data-alt-text') || '').toLowerCase();
        var mobileDisplay = alt.indexOf('mobile-display') !== -1;
        this.hidden = isPreview ? false : (isMobile ? !mobileDisplay : mobileDisplay);
      });

      $gallery.find('carousel-slider').each(function () {
        if (this.offsetParent && typeof this.refresh === 'function') this.refresh();
      });

      if (viewportChanged) this.selectPreviewFirst();
    },

    injectPreviewSlide: function () {
      var $gallery = $('media-gallery.media-gallery, .media-gallery').first();
      if (!$gallery.length) return;
      $gallery.addClass('jsc-np-media-gallery');
      if (this.config.stickyGalleryEnable !== false) {
        $gallery.addClass('jsc-np-sticky-gallery');
      }
      $('body').addClass('jsc-np-page');
      $('product-form.js-product').addClass('jsc-np-product-form');

      var $grid = $gallery.find('.slider__grid').first();
      if (!$grid.length) return;
      if ($gallery.find('[data-np-preview]').length) return;

      var previewMediaId = 'np-preview-' + this.config.currentProductId;
      var $preview = $(
        '<div class="jsc-np-preview" data-np-preview>' +
          '<div class="jsc-np-preview__stack">' +
            '<img class="jsc-np-preview__layer jsc-np-preview__cover" data-np-layer="cover" alt="" />' +
            '<img class="jsc-np-preview__layer jsc-np-preview__insert" data-np-layer="insert" alt="" hidden />' +
            '<div class="jsc-np-preview__charms" data-np-layer="charms"></div>' +
            '<div class="jsc-np-preview__accessories" data-np-layer="accessories"></div>' +
            '<span class="jsc-np-preview__monogram jsc-np-deboss" data-np-layer="monogram" hidden></span>' +
          '</div>' +
        '</div>'
      );

      // `.ar-contain` reserves height from --aspect-ratio; without it the
      // slide collapses. Seeded with the theme's min_aspect_ratio and
      // corrected once the cover overlay reports its natural size.
      var $slide = $(
        '<div class="slider__item is-active jsc-np-preview-slide" role="group" data-media-id="' + previewMediaId + '" data-np-base-slide="true">' +
          '<div class="product-media product-media--image">' +
            '<div class="ar-contain jsc-np-preview-host" style="--aspect-ratio: ' + NP_MIN_ASPECT_RATIO + '"></div>' +
          '</div>' +
        '</div>'
      );
      var $previewHost = $slide.find('.jsc-np-preview-host');
      // Preview layers charms onto the cover, so opening zoom from this slide
      // would show a flat overlay and look like a different product.
      $previewHost.append($preview);
      $grid.find('.slider__item').removeClass('is-active');
      $grid.prepend($slide);

      var $thumbGrid = $gallery.find('.thumbnails .slider__grid').first();
      if ($thumbGrid.length) {
        $thumbGrid.find('.slider__item').removeClass('is-active');
        $thumbGrid.prepend(
          '<li class="slider__item is-active jsc-np-preview-thumbnail" data-media-id="' + previewMediaId + '">' +
            '<a class="thumbnail thumbnail--media-image" href="#">' +
              '<img class="theme-img" alt="Notebook personalisation preview">' +
            '</a>' +
          '</li>'
        );
      }

      var galleryEl = $gallery[0];
      if (galleryEl) {
        galleryEl.mediaLists = [
          galleryEl.querySelectorAll('.main-image .slider__item'),
          galleryEl.querySelectorAll('.product-media-collage__item'),
          galleryEl.querySelectorAll('.thumbnails .slider__item')
        ];
        if (galleryEl.galleryModal) {
          galleryEl.mediaLists.push(galleryEl.galleryModal.querySelectorAll('.gallery-viewer__thumb'));
        }
      }
      $gallery.find('carousel-slider').each(function () {
        if (typeof this.refresh === 'function') this.refresh();
      });
    },

    // `retry` re-runs the selection once `carousel-slider` upgrades, which is
    // only needed before first paint; a call made from a customer interaction
    // would otherwise pull the gallery back 100ms after they swipe away.
    selectPreviewFirst: function (options) {
      var opts = options || {};
      var transition = opts.transition || 'instant';
      var retry = opts.retry !== false;
      var previewMediaId = 'np-preview-' + this.config.currentProductId;

      function select() {
        var $gallery = $('media-gallery.jsc-np-media-gallery, .jsc-np-media-gallery').first();
        if (!$gallery.length) return;

        var $mainItems = $gallery.find('.main-image .slider__item');
        var $thumbItems = $gallery.find('.thumbnails .slider__item');
        var $previewSlide = $mainItems.filter('[data-media-id="' + previewMediaId + '"]');
        var $previewThumb = $thumbItems.filter('[data-media-id="' + previewMediaId + '"]');
        if (!$previewSlide.length) return;

        $mainItems.removeClass('is-active').attr('tabindex', '-1');
        $previewSlide.addClass('is-active').removeAttr('tabindex');
        $thumbItems.removeClass('is-active');
        $previewThumb.addClass('is-active');

        var mainSlider = $previewSlide.closest('carousel-slider')[0];
        if (mainSlider && typeof mainSlider.scrollToElement === 'function') {
          mainSlider.scrollToElement($previewSlide[0], transition);
        } else {
          var slider = $previewSlide.closest('.slider')[0];
          if (slider) slider.scrollLeft = 0;
        }

        var thumbSlider = $previewThumb.closest('carousel-slider')[0];
        if (thumbSlider && typeof thumbSlider.scrollToElement === 'function') {
          thumbSlider.scrollToElement($previewThumb[0], transition);
        }
      }

      select();
      if (!retry) return;
      if (window.customElements && window.customElements.whenDefined) {
        window.customElements.whenDefined('carousel-slider').then(function () {
          window.requestAnimationFrame(select);
          window.setTimeout(select, 100);
        });
      } else {
        window.setTimeout(select, 100);
      }
    },

    bindGlobal: function () {
      var self = this;
      var $r = this.$root;

      if (!NP.NotebookPersonalisation._stickyAtcBound) {
        NP.NotebookPersonalisation._stickyAtcBound = true;
        document.addEventListener(
          'click',
          function (evt) {
            var btn = evt.target && evt.target.closest
              ? evt.target.closest('.jsc-sticky-atc-btn, [data-np-sticky-prev]')
              : null;
            if (!btn) return;
            var isNp =
              document.documentElement.classList.contains('jsc-np-page') ||
              document.body.classList.contains('jsc-np-page');
            if (!isNp) return;
            // A re-rendered section leaves earlier instances detached, so always use the live one.
            var app = NP.NotebookPersonalisation.current;
            if (!app || !app.$root || !document.contains(app.$root[0])) return;
            evt.preventDefault();
            evt.stopImmediatePropagation();
            if (btn.hasAttribute('data-np-sticky-prev')) {
              app.handleStickyPrevClick();
              return;
            }
            app.handleStickyAtcClick(btn);
          },
          true
        );

        // The theme rebuilds and relabels the sticky bar of its own accord, so claim it back after.
        var resync = function () {
          var app = NP.NotebookPersonalisation.current;
          if (app && app.state) app.syncStickyAtc(app.pricing());
        };
        $(window).on('load', resync);
        document.addEventListener('on:variant:change', resync);
        window.setTimeout(resync, 0);
      }

      $r.on('click', '[data-np-next]', function () {
        self.handleNext();
      });
      $r.on('click', '[data-np-prev]', function () {
        self.goToStep(Math.max(0, self.state.currentStep - 1));
      });
      $r.on('click', '[data-np-stepper] [data-step]', function () {
        var target = parseInt($(this).attr('data-step'), 10);
        if (isNaN(target) || target > self.state.maxStepReached) return;
        if (target === self.state.currentStep) return;

        // Backward navigation stays free; forward requires each step still valid.
        if (target < self.state.currentStep) {
          self.goToStep(target);
          return;
        }

        for (var s = self.state.currentStep; s < target; s++) {
          if (!self.isStepComplete(s)) {
            if (s !== self.state.currentStep) self.goToStep(s);
            self.validateStep(s);
            self.clampMaxStepReached();
            self.renderStepper();
            self.persist();
            return;
          }
        }
        self.goToStep(target);
      });

      $r.on('click', '[data-np-cover-swatches] .jsc-np-swatch', function () {
        if ($(this).hasClass('is-disabled') || $(this).prop('disabled')) return;
        var id = Number($(this).attr('data-id'));
        var cover = null;
        (self.config.covers || []).forEach(function (c) {
          if (c.id === id) cover = c;
        });
        if (!cover || !self.isProductAvailable(cover)) return;
        if (cover.id === self.config.currentProductId) {
          self.state.coverId = cover.id;
          self.renderCover();
          self.renderPreview();
          self.renderPrice();
          self.persist();
          return;
        }
        self.state.coverId = cover.id;
        self.persist();
        window.location.href = self.urlWithParams(cover.url);
      });

      $r.on('change', '[data-np-insert-select]', function () {
        var raw = $(this).val();
        if (raw === '' || raw == null) {
          self.state.insertIndex = null;
          self.$root.find('[data-np-insert-error]').prop('hidden', true);
          self.renderInsert();
          self.renderPreview();
          self.renderPrice();
          self.persist();
          return;
        }
        var nextIndex = Number(raw);
        var insert = null;
        (self.config.inserts || []).forEach(function (ins) {
          if (ins.index === nextIndex) insert = ins;
        });
        if (!insert || !self.isProductAvailable(insert.product)) {
          self.sanitizeUnavailableSelections();
          self.renderInsert();
          return;
        }
        self.state.insertIndex = nextIndex;
        self.$root.find('[data-np-insert-error]').prop('hidden', true);
        self.renderInsert();
        self.renderPreview();
        self.renderPrice();
        self.persist();
      });

      $r.on('click', '[data-np-insert-viewer-open]', function () {
        self.openInsertViewer(this);
      });

      $r.on('click', '[data-np-insert-viewer-close]', function () {
        self.closeInsertViewer();
      });

      $r.on('click', '[data-np-insert-viewer-prev]', function () {
        self.changeInsertViewerImage(-1);
      });

      $r.on('click', '[data-np-insert-viewer-next]', function () {
        self.changeInsertViewerImage(1);
      });

      $r.on('keydown', '[data-np-insert-viewer]', function (event) {
        if (!self.insertViewerActive || (self.insertViewerImages || []).length < 2) return;
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          self.changeInsertViewerImage(-1);
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          self.changeInsertViewerImage(1);
        }
      });

      $r.on('click', '[data-np-insert-viewer]', function (event) {
        if (event.target === this) self.closeInsertViewer();
      });

      $r.find('[data-np-insert-viewer]').on('close', function () {
        self.cleanupInsertViewer();
      });

      $r.on('click', '[data-np-addons-toggle]', function () {
        var collapsed = $r.find('[data-np-addons-block]').hasClass('is-collapsed');
        if (collapsed && self.state.noAddOns) {
          self.state.noAddOns = false;
          self.renderAddons();
          self.renderPreview();
          self.renderPrice();
          self.persist();
          return;
        }
        self.setAddonsCollapsed(!collapsed);
      });

      $r.on('change', '[data-np-no-addons]', function () {
        self.state.noAddOns = $(this).is(':checked');
        if (self.state.noAddOns) {
          self.state.addCharms = false;
          self.state.tpbCharmIds = [];
          self.state.regularCharmIds = [];
          self.state.charmOrderIds = [];
          self.state.accessoryIds = [];
          $r.find('[data-np-add-charms]').prop('checked', false);
          $r.find('[data-np-addons-choice-error]').prop('hidden', true);
        }
        self.renderAddons();
        self.renderPreview();
        self.renderPrice();
        self.persist();
      });

      $r.on('change', '[data-np-add-charms]', function () {
        if (!self.isCharmsFeatureAvailable()) {
          $(this).prop('checked', false);
          self.state.addCharms = false;
          return;
        }
        self.state.addCharms = $(this).is(':checked');
        if (self.state.addCharms) {
          self.state.noAddOns = false;
          $r.find('[data-np-no-addons]').prop('checked', false);
          $r.find('[data-np-addons-choice-error]').prop('hidden', true);
        } else {
          self.state.tpbCharmIds = [];
          self.state.regularCharmIds = [];
          self.state.charmOrderIds = [];
        }
        $r.find('[data-np-charms-error]').prop('hidden', true);
        self.renderAddons();
        self.renderPreview();
        // Charm overlays only exist on the preview slide, so a customer who has
        // swiped to another gallery image would see nothing change.
        self.selectPreviewFirst({ transition: 'smooth', retry: false });
        self.renderPrice();
        self.persist();
      });

      $r.on('click', '[data-np-tpb-charms] .jsc-np-charm', function () {
        if (self.state.noAddOns) return;
        if (!self.isCharmsFeatureAvailable()) return;
        if ($(this).hasClass('is-disabled') || $(this).prop('disabled')) return;
        var id = Number($(this).attr('data-id'));
        var product = null;
        (self.config.tpbCharms.products || []).forEach(function (p) {
          if (p.id === id) product = p;
        });
        if (!self.isProductAvailable(product)) return;
        self.toggleCharmSelection('tpbCharmIds', id, self.config.tpbCharms.limit || 1);
        var hasCharms =
          self.state.tpbCharmIds.length || self.state.regularCharmIds.length;
        self.state.addCharms = !!hasCharms;
        if (hasCharms) {
          self.state.noAddOns = false;
          $r.find('[data-np-addons-choice-error]').prop('hidden', true);
        } else {
          self.state.charmOrderIds = [];
        }
        $r.find('[data-np-charms-error]').prop('hidden', true);
        self.renderAddons();
        self.renderPreview();
        self.selectPreviewFirst({ transition: 'smooth', retry: false });
        self.renderPrice();
        self.persist();
      });

      $r.on('click', '[data-np-regular-charms] .jsc-np-charm', function () {
        if (self.state.noAddOns) return;
        if (!self.isCharmsFeatureAvailable()) return;
        if ($(this).hasClass('is-disabled') || $(this).prop('disabled')) return;
        var id = Number($(this).attr('data-id'));
        var product = null;
        (self.config.regularCharms.products || []).forEach(function (p) {
          if (p.id === id) product = p;
        });
        if (!self.isProductAvailable(product)) return;
        self.toggleCharmSelection('regularCharmIds', id, self.config.regularCharms.limit || 2);
        var hasCharms =
          self.state.tpbCharmIds.length || self.state.regularCharmIds.length;
        self.state.addCharms = !!hasCharms;
        if (hasCharms) {
          self.state.noAddOns = false;
          $r.find('[data-np-addons-choice-error]').prop('hidden', true);
        } else {
          self.state.charmOrderIds = [];
        }
        $r.find('[data-np-charms-error]').prop('hidden', true);
        self.renderAddons();
        self.renderPreview();
        self.renderPrice();
        self.persist();
      });

      $r.on('change', '[data-np-accessory]', function () {
        if (self.state.noAddOns) return;
        var id = Number($(this).attr('data-id'));
        var product = null;
        (self.config.accessories || []).forEach(function (p) {
          if (p.id === id) product = p;
        });
        if (!self.isProductAvailable(product)) {
          $(this).prop('checked', false);
          return;
        }
        var checked = $(this).is(':checked');
        var idx = self.state.accessoryIds.indexOf(id);
        if (checked && idx === -1) {
          self.state.accessoryIds.push(id);
          self.state.noAddOns = false;
          $r.find('[data-np-no-addons]').prop('checked', false);
          $r.find('[data-np-addons-choice-error]').prop('hidden', true);
        } else if (!checked && idx > -1) {
          self.state.accessoryIds.splice(idx, 1);
        }
        self.renderAddons();
        self.renderPreview();
        self.renderPrice();
        self.persist();
      });

      $r.on('change', '[data-np-no-monogram]', function () {
        self.state.noMonogram = $(this).is(':checked');
        if (self.state.noMonogram) {
          self.state.wantMonogram = false;
          self.state.monogramText = '';
          self.state.monogramFontId = null;
          self.state.monogramTerms = false;
          $r.find('[data-np-want-monogram]').prop('checked', false);
          $r.find('[data-np-tnc]').prop('checked', false);
          $r
            .find(
              '[data-np-monogram-choice-error],[data-np-mono-text-error],[data-np-tnc-error]'
            )
            .prop('hidden', true);
        }
        self.renderMonogram();
        self.renderPreview();
        self.selectPreviewFirst({ transition: 'smooth', retry: false });
        self.renderPrice();
        self.persist();
      });

      $r.on('change', '[data-np-want-monogram]', function () {
        if (!self.isMonogramProductAvailable()) {
          $(this).prop('checked', false);
          self.state.wantMonogram = false;
          self.state.noMonogram = true;
          $r.find('[data-np-monogram-choice-error]').prop('hidden', true);
          self.renderMonogram();
          self.renderPreview();
          self.renderPrice();
          self.persist();
          return;
        }
        self.state.wantMonogram = $(this).is(':checked');
        if (self.state.wantMonogram) {
          self.state.noMonogram = false;
          $r.find('[data-np-no-monogram]').prop('checked', false);
          $r.find('[data-np-monogram-choice-error]').prop('hidden', true);
          self.ensureDefaults();
        } else {
          self.state.monogramText = '';
          self.state.monogramFontId = null;
          self.state.monogramTerms = false;
        }
        self.renderMonogram();
        self.renderPreview();
        self.renderPrice();
        self.persist();
      });

      $r.on('click', '[data-np-keyboard] button', function () {
        if (self.state.noMonogram || !self.isMonogramProductAvailable()) return;
        self.state.wantMonogram = true;
        self.state.noMonogram = false;
        $r.find('[data-np-monogram-choice-error]').prop('hidden', true);
        self.ensureDefaults();
        var key = $(this).attr('data-np-key') || $(this).text();
        var max = self.getMonogramCharLimit();
        var text = self.state.monogramText || '';
        if (key === 'CLEAR') text = '';
        else if (key === 'BACKSPACE') text = text.slice(0, -1);
        else if (text.length < max) text += key;
        self.state.monogramText = self.sanitizeMonogramText(text);
        $r.find('[data-np-mono-text-error]').prop('hidden', true);
        self.renderMonogram();
        self.renderPreview();
        self.selectPreviewFirst({ transition: 'smooth', retry: false });
        self.renderPrice();
        self.persist();
      });

      $r.on('click', '[data-np-fonts] .jsc-np-option-btn', function () {
        if (self.state.noMonogram || !self.isMonogramProductAvailable()) return;
        self.state.wantMonogram = true;
        self.state.noMonogram = false;
        $r.find('[data-np-monogram-choice-error]').prop('hidden', true);
        self.ensureDefaults();
        self.state.monogramFontId = Number($(this).attr('data-id'));
        self.state.monogramText = self.sanitizeMonogramText(self.state.monogramText);
        self.renderMonogram();
        self.renderPreview();
        self.selectPreviewFirst({ transition: 'smooth', retry: false });
        self.renderPrice();
        self.persist();
      });

      $r.on('change', '[data-np-tnc]', function () {
        self.state.monogramTerms = $(this).is(':checked');
        if (self.state.monogramTerms) $r.find('[data-np-tnc-error]').prop('hidden', true);
        self.persist();
      });

      $(document).on('on:cart:add.np', function () {
        self.clearParams();
      });
      document.addEventListener('on:cart:add', function () {
        self.clearParams();
      });

      // Capture on document so this runs before the theme's own submit listener on the form.
      if (!document.documentElement.hasAttribute('data-np-submit-bound')) {
        document.documentElement.setAttribute('data-np-submit-bound', '1');
        document.addEventListener('submit', function (evt) {
          self.handleProductFormSubmit(evt);
        }, true);
      }

      $(window).on('resize.np', function () {
        self.renderPreview();
        self.filterNpGalleryByViewport();
        self.syncStickyOffsets();
        var insertMediaMobile = self.isInsertMediaMobile();
        if (self.npInsertMediaMobile !== insertMediaMobile) {
          self.npInsertMediaMobile = insertMediaMobile;
          self.renderInsert();
          if (self.insertViewerActive) {
            self.insertViewerImages = self.insertViewerSources(self.getInsertForDetail());
            self.showInsertViewerImage(0);
          }
        }
        if (!self.insertViewerActive) return;
        if (self.insertViewerResizeTimer) clearTimeout(self.insertViewerResizeTimer);
        self.insertViewerResizeTimer = setTimeout(function () {
          self.insertViewerResizeTimer = null;
          if (self.insertViewerActive) self.resetInsertViewerZoomFit();
        }, 100);
      });
    },

    openInsertViewer: function (trigger) {
      var dialog = this.$root.find('[data-np-insert-viewer]')[0];
      if (!dialog) return;

      var insert = this.getInsertForDetail();
      this.insertViewerImages = this.insertViewerSources(insert);
      this.showInsertViewerImage(0);

      this.insertViewerActive = true;
      this.insertViewerReturnFocus = trigger || document.activeElement;
      this.insertViewerOrientationQuery = window.matchMedia('(orientation: portrait)');
      this.insertViewerOrientationHandler = this.updateInsertViewerOrientation.bind(this);
      this.insertViewerIsMobile =
        window.matchMedia('(max-width: 767px)').matches ||
        window.matchMedia('(pointer: coarse)').matches;

      window.addEventListener('orientationchange', this.insertViewerOrientationHandler);
      if (this.insertViewerOrientationQuery.addEventListener) {
        this.insertViewerOrientationQuery.addEventListener('change', this.insertViewerOrientationHandler);
      } else if (this.insertViewerOrientationQuery.addListener) {
        this.insertViewerOrientationQuery.addListener(this.insertViewerOrientationHandler);
      }

      document.documentElement.classList.add('jsc-np-insert-viewer-open');
      document.body.classList.add('jsc-np-insert-viewer-open');

      if (typeof dialog.showModal === 'function') {
        if (!dialog.open) dialog.showModal();
      } else {
        dialog.setAttribute('open', '');
      }
      this.initInsertViewerZoom();
      this.updateInsertViewerOrientation();
    },

    updateInsertViewerOrientation: function () {
      var isPortrait = window.matchMedia('(orientation: portrait)').matches;
      var showRotate = this.insertViewerIsMobile && isPortrait;
      this.$root.find('[data-np-insert-rotate]').prop('hidden', !showRotate);
      if (!showRotate) {
        this.resetInsertViewerZoomFit();
      }
    },

    closeInsertViewer: function () {
      var dialog = this.$root.find('[data-np-insert-viewer]')[0];
      if (!dialog) return;
      if (typeof dialog.close === 'function' && dialog.open) {
        dialog.close();
      } else {
        dialog.removeAttribute('open');
      }
      this.cleanupInsertViewer();
    },

    cleanupInsertViewer: function () {
      if (this.insertViewerResizeTimer) {
        clearTimeout(this.insertViewerResizeTimer);
        this.insertViewerResizeTimer = null;
      }

      if (!this.insertViewerActive && !this.insertViewerOrientationHandler && !this.insertViewerReturnFocus) {
        document.documentElement.classList.remove('jsc-np-insert-viewer-open');
        document.body.classList.remove('jsc-np-insert-viewer-open');
        return;
      }

      this.destroyInsertViewerZoom();

      if (this.insertViewerOrientationHandler) {
        window.removeEventListener('orientationchange', this.insertViewerOrientationHandler);
        if (this.insertViewerOrientationQuery && this.insertViewerOrientationQuery.removeEventListener) {
          this.insertViewerOrientationQuery.removeEventListener('change', this.insertViewerOrientationHandler);
        } else if (this.insertViewerOrientationQuery && this.insertViewerOrientationQuery.removeListener) {
          this.insertViewerOrientationQuery.removeListener(this.insertViewerOrientationHandler);
        }
      }

      this.insertViewerOrientationHandler = null;
      this.insertViewerOrientationQuery = null;
      this.insertViewerIsMobile = false;
      this.insertViewerActive = false;
      this.insertViewerImages = [];
      this.insertViewerImageIndex = 0;
      document.documentElement.classList.remove('jsc-np-insert-viewer-open');
      document.body.classList.remove('jsc-np-insert-viewer-open');

      if (this.insertViewerReturnFocus && typeof this.insertViewerReturnFocus.focus === 'function') {
        this.insertViewerReturnFocus.focus();
      }
      this.insertViewerReturnFocus = null;
    },

    initInsertViewerZoom: function () {
      var zoomEl = this.$root.find('[data-np-insert-zoom]')[0];
      var img = this.$root.find('[data-np-insert-viewer-image]')[0];
      if (!zoomEl || !img) return;

      this.destroyInsertViewerZoom();

      this.insertViewerZoom = {
        container: zoomEl,
        image: img,
        transform: { panX: 0, panY: 0, zoom: 1 },
        pinchTracking: { isTracking: false, lastPinchDistance: 0 },
        touchTracking: { isTracking: false, lastTouchX: 0, lastTouchY: 0 },
        swipeTracking: { isTracking: false, startX: 0, startY: 0, didSwipe: false },
        wheelZoomMultiplier: -0.001,
        pinchZoomMultiplier: 0.003,
        touchPanModifier: 1
      };

      var self = this;
      this.insertViewerZoom.onClick = function (evt) {
        self.onInsertViewerZoomClick(evt);
      };
      this.insertViewerZoom.onTouchStart = function (evt) {
        self.startInsertViewerSwipe(evt);
      };
      this.insertViewerZoom.onTouchEnd = function (evt) {
        self.endInsertViewerSwipe(evt);
        self.stopInsertViewerTouchTracking();
      };
      this.insertViewerZoom.onTouchMove = function (evt) {
        self.trackInsertViewerInput(evt);
      };
      this.insertViewerZoom.onMouseMove = function (evt) {
        self.trackInsertViewerInput(evt);
      };
      this.insertViewerZoom.onWheel = function (evt) {
        self.trackInsertViewerWheel(evt);
      };
      this.insertViewerZoom.onLoad = function () {
        self.resetInsertViewerZoomFit();
      };

      zoomEl.addEventListener('click', this.insertViewerZoom.onClick);
      zoomEl.addEventListener('touchstart', this.insertViewerZoom.onTouchStart, { passive: true });
      zoomEl.addEventListener('touchend', this.insertViewerZoom.onTouchEnd);
      zoomEl.addEventListener('touchmove', this.insertViewerZoom.onTouchMove, { passive: false });
      zoomEl.addEventListener('mousemove', this.insertViewerZoom.onMouseMove);
      zoomEl.addEventListener('wheel', this.insertViewerZoom.onWheel, { passive: false });
      img.addEventListener('load', this.insertViewerZoom.onLoad);

      if (img.complete && img.naturalWidth) {
        this.resetInsertViewerZoomFit();
      }
    },

    destroyInsertViewerZoom: function () {
      var zoom = this.insertViewerZoom;
      if (!zoom) return;

      if (zoom.container) {
        zoom.container.removeEventListener('click', zoom.onClick);
        zoom.container.removeEventListener('touchstart', zoom.onTouchStart);
        zoom.container.removeEventListener('touchend', zoom.onTouchEnd);
        zoom.container.removeEventListener('touchmove', zoom.onTouchMove);
        zoom.container.removeEventListener('mousemove', zoom.onMouseMove);
        zoom.container.removeEventListener('wheel', zoom.onWheel);
      }
      if (zoom.image && zoom.onLoad) {
        zoom.image.removeEventListener('load', zoom.onLoad);
        zoom.image.style.transform = '';
        zoom.image.style.top = '';
        zoom.image.style.left = '';
        zoom.image.style.visibility = '';
      }
      this.insertViewerZoom = null;
    },

    resetInsertViewerZoomFit: function () {
      var zoom = this.insertViewerZoom;
      if (!zoom || !zoom.image || !zoom.container) return;
      var img = zoom.image;
      var container = zoom.container;
      if (!img.naturalWidth || !img.naturalHeight) return;

      img.style.top = container.clientHeight / 2 - img.clientHeight / 2 + 'px';
      img.style.left = container.clientWidth / 2 - img.clientWidth / 2 + 'px';
      this.setInsertViewerTransform(0, 0, 0);
      img.style.visibility = '';
    },

    // Contain-fit scale: the whole insert visible inside the viewer.
    getInsertViewerFitZoom: function () {
      var zoom = this.insertViewerZoom;
      if (!zoom || !zoom.image || !zoom.container) return 0;
      var img = zoom.image;
      var container = zoom.container;
      if (!img.naturalWidth || !img.naturalHeight) return 0;
      return Math.min(container.clientWidth / img.naturalWidth, container.clientHeight / img.naturalHeight);
    },

    getInsertViewerMaxZoom: function () {
      return 1;
    },

    setInsertViewerTransform: function (panX, panY, zoomLevel) {
      var zoom = this.insertViewerZoom;
      if (!zoom) return;
      zoom.transform.panX = panX;
      zoom.transform.panY = panY;
      zoom.transform.zoom = zoomLevel;
      this.alterInsertViewerZoomBy(0);
    },

    alterInsertViewerZoomBy: function (delta) {
      var zoom = this.insertViewerZoom;
      if (!zoom || !zoom.image || !zoom.container) return;

      zoom.transform.zoom += delta;
      // Do not zoom out further than contain-fit, nor in further than native size.
      zoom.transform.zoom = Math.max(zoom.transform.zoom, this.getInsertViewerFitZoom());
      zoom.transform.zoom = Math.min(zoom.transform.zoom, this.getInsertViewerMaxZoom());

      this.alterInsertViewerPanBy(0, 0);
      this.updateInsertViewerZoomPosition();
    },

    alterInsertViewerPanBy: function (x, y) {
      var zoom = this.insertViewerZoom;
      if (!zoom || !zoom.image || !zoom.container) return;
      var img = zoom.image;
      var container = zoom.container;

      zoom.transform.panX += x;
      var panXMax = Math.max((img.naturalWidth * zoom.transform.zoom - container.clientWidth) / 2, 0);
      zoom.transform.panX = Math.min(Math.max(zoom.transform.panX, -panXMax), panXMax);

      zoom.transform.panY += y;
      var panYMax = Math.max((img.naturalHeight * zoom.transform.zoom - container.clientHeight) / 2, 0);
      zoom.transform.panY = Math.min(Math.max(zoom.transform.panY, -panYMax), panYMax);

      this.updateInsertViewerZoomPosition();
    },

    panInsertViewerFromCoordinate: function (inputX, inputY) {
      var zoom = this.insertViewerZoom;
      if (!zoom || !zoom.image || !zoom.container) return;
      var img = zoom.image;
      var container = zoom.container;
      // Measure the scaled image so the cursor maps across the visible overflow only.
      var scaledWidth = img.naturalWidth * zoom.transform.zoom;
      var scaledHeight = img.naturalHeight * zoom.transform.zoom;
      var doPanX = scaledWidth > container.clientWidth;
      var doPanY = scaledHeight > container.clientHeight;
      if (!doPanX && !doPanY) return;

      var midX = container.clientWidth / 2;
      var midY = container.clientHeight / 2;
      var offsetFromCentreX = inputX - midX;
      var offsetFromCentreY = inputY - midY;
      var finalOffsetX = 0;
      var finalOffsetY = 0;

      if (doPanX) {
        finalOffsetX = Math.round(-offsetFromCentreX * ((scaledWidth - container.clientWidth) / 2) / midX);
      }
      if (doPanY) {
        finalOffsetY = Math.round(-offsetFromCentreY * ((scaledHeight - container.clientHeight) / 2) / midY);
      }

      zoom.transform.panX = finalOffsetX;
      zoom.transform.panY = finalOffsetY;
      this.alterInsertViewerPanBy(0, 0);
    },

    updateInsertViewerZoomPosition: function () {
      var zoom = this.insertViewerZoom;
      if (!zoom || !zoom.image) return;
      zoom.image.style.transform =
        'translate3d(' + zoom.transform.panX + 'px, ' + zoom.transform.panY + 'px, 0) scale(' + zoom.transform.zoom + ')';
    },

    onInsertViewerZoomClick: function (evt) {
      var zoom = this.insertViewerZoom;
      if (!zoom) return;
      evt.preventDefault();

      if (zoom.swipeTracking.didSwipe) {
        zoom.swipeTracking.didSwipe = false;
        return;
      }

      // Mobile keeps pinch-to-zoom only; a tap must not toggle zoom.
      if (this.insertViewerIsMobile) return;

      if (zoom.transform.zoom === this.getInsertViewerMaxZoom()) {
        zoom.transform.zoom = 0;
        this.alterInsertViewerZoomBy(0);
      } else {
        zoom.transform.zoom = this.getInsertViewerMaxZoom();
        this.alterInsertViewerZoomBy(0);
        this.panInsertViewerFromCoordinate(evt.clientX, evt.clientY);
      }
    },

    stopInsertViewerTouchTracking: function () {
      var zoom = this.insertViewerZoom;
      if (!zoom) return;
      zoom.pinchTracking.isTracking = false;
      zoom.touchTracking.isTracking = false;
    },

    // At contain-fit there is nothing to pan, so a horizontal drag changes
    // image instead. Once zoomed past fit the drag stays a pan.
    isInsertViewerAtFitZoom: function () {
      var zoom = this.insertViewerZoom;
      if (!zoom) return false;
      var fit = this.getInsertViewerFitZoom();
      if (!fit) return false;
      return Math.abs(zoom.transform.zoom - fit) < 0.001;
    },

    startInsertViewerSwipe: function (evt) {
      var zoom = this.insertViewerZoom;
      if (!zoom) return;
      var swipe = zoom.swipeTracking;
      swipe.didSwipe = false;
      if (
        !evt.touches ||
        evt.touches.length !== 1 ||
        (this.insertViewerImages || []).length < 2 ||
        !this.isInsertViewerAtFitZoom()
      ) {
        swipe.isTracking = false;
        return;
      }
      swipe.isTracking = true;
      swipe.startX = evt.touches[0].clientX;
      swipe.startY = evt.touches[0].clientY;
    },

    endInsertViewerSwipe: function (evt) {
      var zoom = this.insertViewerZoom;
      if (!zoom) return;
      var swipe = zoom.swipeTracking;
      if (!swipe.isTracking) return;
      swipe.isTracking = false;
      if (!evt || !evt.changedTouches || evt.changedTouches.length !== 1) return;

      var dx = evt.changedTouches[0].clientX - swipe.startX;
      var dy = evt.changedTouches[0].clientY - swipe.startY;
      if (Math.abs(dx) < NP_SWIPE_MIN_DISTANCE || Math.abs(dx) <= Math.abs(dy)) return;

      // Touch still emits a click after the drag, which would otherwise run
      // tap-to-zoom on the image we just swiped to.
      swipe.didSwipe = true;
      this.changeInsertViewerImage(dx < 0 ? 1 : -1);
    },

    trackInsertViewerInput: function (evt) {
      var zoom = this.insertViewerZoom;
      if (!zoom) return;
      evt.preventDefault();

      if (evt.type === 'touchmove' && evt.touches && evt.touches.length > 0) {
        var touch1 = evt.touches[0];
        if (!zoom.touchTracking.isTracking) {
          zoom.touchTracking.isTracking = true;
          zoom.touchTracking.lastTouchX = touch1.clientX;
          zoom.touchTracking.lastTouchY = touch1.clientY;
        } else {
          this.alterInsertViewerPanBy(
            (touch1.clientX - zoom.touchTracking.lastTouchX) * zoom.touchPanModifier,
            (touch1.clientY - zoom.touchTracking.lastTouchY) * zoom.touchPanModifier
          );
          zoom.touchTracking.lastTouchX = touch1.clientX;
          zoom.touchTracking.lastTouchY = touch1.clientY;
        }

        if (evt.touches.length === 2) {
          var touch2 = evt.touches[1];
          var pinchDistance = Math.sqrt(
            Math.pow(touch1.clientX - touch2.clientX, 2) + Math.pow(touch1.clientY - touch2.clientY, 2)
          );
          if (!zoom.pinchTracking.isTracking) {
            zoom.pinchTracking.lastPinchDistance = pinchDistance;
            zoom.pinchTracking.isTracking = true;
          } else {
            this.alterInsertViewerZoomBy(
              (pinchDistance - zoom.pinchTracking.lastPinchDistance) * zoom.pinchZoomMultiplier
            );
            zoom.pinchTracking.lastPinchDistance = pinchDistance;
          }
        } else {
          zoom.pinchTracking.isTracking = false;
        }
      } else {
        this.panInsertViewerFromCoordinate(evt.clientX, evt.clientY);
      }
    },

    trackInsertViewerWheel: function (evt) {
      var zoom = this.insertViewerZoom;
      if (!zoom) return;
      evt.preventDefault();

      if (evt.deltaY !== 0) {
        this.alterInsertViewerZoomBy(evt.deltaY * zoom.wheelZoomMultiplier);
      }
    },

    canAdvanceFromStep: function (step) {
      if (step === 0) return this.isCurrentCoverAvailable();
      if (step === 1) return this.hasAnyAvailableInsert();
      return true;
    },

    // Silent completeness check used to lock the stepper after selections change.
    isStepComplete: function (step) {
      if (!this.canAdvanceFromStep(step)) return false;
      if (step === 1) {
        return this.state.insertIndex != null && !!this.getInsert();
      }
      if (step === 2) {
        var wantsAddons =
          !!this.state.addCharms || (this.state.accessoryIds || []).length > 0;
        if (!this.state.noAddOns && !wantsAddons) return false;
        if (this.state.noAddOns || !this.state.addCharms) return true;
        var required = this.charmRequirements();
        if ((this.state.tpbCharmIds || []).length < required.tpb) return false;
        if ((this.state.regularCharmIds || []).length < required.regular) return false;
        return true;
      }
      return true;
    },

    validateStep: function (step) {
      if (!this.canAdvanceFromStep(step)) {
        this.updateStepActions();
        return false;
      }
      if (step === 1) return this.validateInsert();
      if (step === 2) return this.validateAddons();
      return true;
    },

    // Keep later steps unlocked only while every earlier step remains complete.
    clampMaxStepReached: function () {
      var max = Math.min(this.state.maxStepReached || 0, STEPS.length - 1);
      var farthest = 0;
      for (var j = 0; j <= max; j++) {
        farthest = j;
        if (j < max && !this.isStepComplete(j)) break;
      }
      this.state.maxStepReached = farthest;
      return farthest;
    },

    updateStepActions: function () {
      var step = this.state.currentStep;
      var canAdvance = this.canAdvanceFromStep(step);
      this.$root.find('[data-np-next]').prop('disabled', !canAdvance).toggleClass('is-sold-out', !canAdvance);
      this.syncStickyAtc(this.pricing());
    },

    handleNext: function () {
      var step = this.state.currentStep;
      if (!this.validateStep(step)) return;
      var next = Math.min(3, step + 1);
      this.state.maxStepReached = Math.max(this.state.maxStepReached, next);
      this.goToStep(next);
    },

    validateInsert: function () {
      if (this.state.insertIndex != null && this.getInsert()) {
        this.$root.find('[data-np-insert-error]').prop('hidden', true);
        return true;
      }
    
      var $error = this.$root.find('[data-np-insert-error]');
    
      $error
        .text('Please select an option')
        .prop('hidden', false);
    
      this.scrollToError($error);
    
      return false;
    },

    scrollToError: function ($error) {
      if (!$error || !$error.length) return;

      // Clears the whole pinned stack on mobile; falls back to a small gap on
      // desktop, where stickyScrollOffset() only reports the theme header.
      var offset = Math.max(this.stickyScrollOffset(), 100);

      $('html, body').animate(
        {
          scrollTop: Math.max(0, $error.offset().top - offset)
        },
        400
      );
    },

    validateAddons: function (skipScroll) {
      var $choiceError = this.$root.find('[data-np-addons-choice-error]');
      var wantsAddons =
        !!this.state.addCharms || (this.state.accessoryIds || []).length > 0;

      if (!this.state.noAddOns && !wantsAddons) {
        $choiceError
          .text(
            (this.config.labels && this.config.labels.addonsChoiceError) ||
              'Please select an option'
          )
          .prop('hidden', false);
        if (!skipScroll) this.scrollToError($choiceError);
        return false;
      }

      $choiceError.prop('hidden', true);
      if (this.state.noAddOns || !this.state.addCharms) return true;
    
      var required = this.charmRequirements();
      var missing = [];
    
      var tpbShort = required.tpb - this.state.tpbCharmIds.length;
      var regularShort = required.regular - this.state.regularCharmIds.length;
    
      var labels = this.config.labels || {};
      var collectibleLabel = labels.collectibleCharm || 'Collectible Design';
      var seasonalLabel = labels.seasonalCharms || 'Seasonal Designs';

      if (tpbShort > 0) {
        missing.push(tpbShort + ' more ' + charmCountLabel(tpbShort, collectibleLabel));
      }
    
      if (regularShort > 0) {
        missing.push(regularShort + ' more ' + charmCountLabel(regularShort, seasonalLabel));
      }
    
      if (!missing.length) {
        this.$root.find('[data-np-charms-error]').prop('hidden', true);
        return true;
      }
    
      var $error = this.$root.find('[data-np-charms-error]');
    
      $error
        .text('Please select ' + missing.join(' and '))
        .prop('hidden', false);
    
      if (!skipScroll) this.scrollToError($error);
    
      return false;
    },

    validateMonogram: function () {
      var $r = this.$root;
      var $choiceError = $r.find('[data-np-monogram-choice-error]');

      if (this.state.noMonogram) {
        $choiceError.prop('hidden', true);
        return true;
      }

      if (!this.state.wantMonogram) {
        $choiceError
          .text(
            (this.config.labels && this.config.labels.monogramChoiceError) ||
              'Please select an option'
          )
          .prop('hidden', false);
        this.scrollToError($choiceError);
        return false;
      }

      $choiceError.prop('hidden', true);
    
      var ok = true;
      var $firstError = $();
    
      // Monogram text validation. The character limit is also the minimum, so a
      // partially filled monogram is rejected.
      var monoText = (this.state.monogramText || '').trim();
      var monoRequired = this.getMonogramCharLimit();

      if (monoText.length !== monoRequired) {
        var $textError = $r.find('[data-np-mono-text-error]');
        var monoLabels = this.config.labels || {};
        var monoMessage = monoLabels.monogramTextError;

        if (monoText.length) {
          monoMessage = String(
            monoLabels.monogramLengthError || 'Please enter exactly [count] characters'
          ).replace('[count]', monoRequired);
        }

        $textError.text(monoMessage).prop('hidden', false);
    
        // Text error gets priority
        $firstError = $textError;
    
        ok = false;
      } else {
        $r.find('[data-np-mono-text-error]').prop('hidden', true);
      }
    
      // Terms & Conditions validation
      if (!this.state.monogramTerms) {
        var $tncError = $r.find('[data-np-tnc-error]');
    
        $tncError
          .text(this.config.labels.monogramTncError)
          .prop('hidden', false);
    
        // Only use T&C if there is no text error
        if (!$firstError.length) {
          $firstError = $tncError;
        }
    
        ok = false;
      } else {
        $r.find('[data-np-tnc-error]').prop('hidden', true);
      }
    
      // Scroll to the first error
      if ($firstError.length) {
        this.scrollToError($firstError);
      }
    
      return ok;
    },

    // The gallery, price block and stepper pin as one stack on mobile, but they
    // live in separate columns so CSS cannot derive the offsets on its own.
    // Publish the measured heights so each one can sit below the previous.
    syncStickyOffsets: function () {
      var root = document.documentElement;
      var gallery = document.querySelector('.jsc-np-sticky-gallery');
      var info = document.querySelector('.jsc-np__sticky-info');
      var stacked = !!gallery && window.getComputedStyle(gallery).position === 'sticky';

      root.style.setProperty(
        '--np-sticky-gallery-h',
        (stacked ? Math.round(gallery.getBoundingClientRect().height) : 0) + 'px'
      );
      root.style.setProperty(
        '--np-sticky-info-h',
        (stacked && info ? Math.round(info.getBoundingClientRect().height) : 0) + 'px'
      );
    },

    // The gallery height follows the cover overlay aspect ratio and the price
    // block reflows with the running total, so re-measure whenever either box
    // changes rather than only on resize.
    observeStickyOffsets: function () {
      this.syncStickyOffsets();
      if (typeof window.ResizeObserver !== 'function') return;

      var self = this;
      if (!this.stickyOffsetObserver) {
        this.stickyOffsetObserver = new window.ResizeObserver(function () {
          self.syncStickyOffsets();
          self.syncStickyStackGlue();
        });
      }

      this.stickyOffsetObserver.disconnect();
      ['.jsc-np-sticky-gallery', '.jsc-np__sticky-info'].forEach(function (selector) {
        var el = document.querySelector(selector);
        if (el) self.stickyOffsetObserver.observe(el);
      });
      this.syncStickyStackGlue();
    },

    // Sticky elements overlap the top of the configurator, so their heights are
    // subtracted to keep the step content visible after scrolling.
    stickyScrollOffset: function () {
      var offset = 0;
      var gallery = document.querySelector('.jsc-np-sticky-gallery');
      if (gallery && window.getComputedStyle(gallery).position === 'sticky') {
        offset += gallery.getBoundingClientRect().height;

        // The price block and stepper pin underneath the gallery, so they eat
        // into the usable viewport too.
        ['.jsc-np__sticky-info', '.jsc-np-stepper'].forEach(function (selector) {
          var el = document.querySelector(selector);
          if (el && window.getComputedStyle(el).position === 'sticky') {
            offset += el.getBoundingClientRect().height;
          }
        });
      }
      var headerHeight = parseFloat(
        window.getComputedStyle(document.documentElement).getPropertyValue('--theme-sticky-header-height')
      );
      if (!isNaN(headerHeight)) offset += headerHeight;
      return offset + 12;
    },

    clearStickyStackGlue: function () {
      ['.jsc-np-sticky-gallery', '.jsc-np__sticky-info', '.jsc-np-stepper'].forEach(function (selector) {
        var el = document.querySelector(selector);
        if (el) el.style.top = '';
      });
    },

    // Separate sticky nodes peel apart at the end of their containers (higher
    // `top` values release first). Re-align their inline `top` so gallery,
    // title/price, and stepper scroll away as one stack.
    syncStickyStackGlue: function () {
      var gallery = document.querySelector('.jsc-np-sticky-gallery');
      var info = document.querySelector('.jsc-np__sticky-info');
      var stepper = document.querySelector('.jsc-np-stepper');
      if (!gallery || !info || !stepper) {
        this.clearStickyStackGlue();
        return;
      }
      if (window.getComputedStyle(gallery).position !== 'sticky') {
        this.clearStickyStackGlue();
        return;
      }

      this.clearStickyStackGlue();

      var chrome = parseFloat(
        window.getComputedStyle(document.documentElement).getPropertyValue('--np-sticky-chrome-h')
      );
      if (isNaN(chrome)) chrome = 0;

      var gH = gallery.getBoundingClientRect().height;
      var iH = info.getBoundingClientRect().height;
      var idealG = chrome;
      var idealI = chrome + gH;
      var idealS = chrome + gH + iH;

      var gTop = gallery.getBoundingClientRect().top;
      var iTop = info.getBoundingClientRect().top;
      var sTop = stepper.getBoundingClientRect().top;
      var push = Math.max(0, idealG - gTop, idealI - iTop, idealS - sTop);

      if (push <= 1) return;

      gallery.style.top = Math.round(idealG - push) + 'px';
      info.style.top = Math.round(idealI - push) + 'px';
      stepper.style.top = Math.round(idealS - push) + 'px';
    },

    bindStickyStackGlue: function () {
      if (NP.NotebookPersonalisation._stickyStackGlueBound) return;
      NP.NotebookPersonalisation._stickyStackGlueBound = true;

      var ticking = false;
      var run = function () {
        ticking = false;
        var app = NP.NotebookPersonalisation.current;
        if (app) app.syncStickyStackGlue();
      };
      var onScrollOrResize = function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(run);
      };

      window.addEventListener('scroll', onScrollOrResize, { passive: true });
      window.addEventListener('resize', onScrollOrResize, { passive: true });
      window.requestAnimationFrame(run);
    },

    scrollToStepTop: function () {
      // The stepper itself pins on mobile, so scrolling to it would be a no-op
      // once stuck. Aim at the panels, which are the first thing that scrolls.
      var anchor =
        this.$root.find('.jsc-np__panels')[0] ||
        this.$root.find('[data-np-stepper]')[0] ||
        this.$root[0];
      if (!anchor || typeof anchor.getBoundingClientRect !== 'function') return;
      var reduceMotion =
        window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      var top = window.pageYOffset + anchor.getBoundingClientRect().top - this.stickyScrollOffset();
      window.scrollTo({
        top: Math.max(top, 0),
        behavior: reduceMotion ? 'auto' : 'smooth'
      });
    },

    goToStep: function (step, skipPersist) {
      this.state.currentStep = step;
      this.$root.find('[data-np-step-panel]').each(function () {
        var s = Number($(this).attr('data-np-step-panel'));
        $(this).prop('hidden', s !== step);
      });
      this.renderStepper();
      this.updateStepActions();
      if (!skipPersist) {
        this.persist();
        var self = this;
        window.requestAnimationFrame(function () {
          self.scrollToStepTop();
        });
      }
    },

    renderAll: function () {
      this.sanitizeUnavailableSelections();
      this.renderStepper();
      this.renderCover();
      this.renderInsert();
      this.renderAddons();
      this.renderMonogram();
      this.renderPreview();
      this.renderPrice();
      this.updateStepActions();
    },

    renderStepper: function () {
      var cur = this.state.currentStep;
      var max = this.state.maxStepReached;
      this.$root.find('[data-np-stepper] [data-step]').each(function () {
        var s = Number($(this).attr('data-step'));
        $(this)
          .toggleClass('is-active', s === cur)
          .toggleClass('is-done', s < max || (s < cur))
          .toggleClass('is-clickable', s <= max);
        $(this).find('.jsc-np-stepper__circle').text('');
      });
      this.$root.find('[data-np-stepper] [data-connector]').each(function () {
        var s = Number($(this).attr('data-connector'));
        $(this).toggleClass('is-done', s < max);
      });
    },

    renderCover: function () {
      var cover = this.getCover();
      var $sw = this.$root.find('[data-np-cover-swatches]').empty();
      var self = this;
      (this.config.covers || []).forEach(function (c) {
        var label = c.title || '';
        var m = label.match(/\(([^)]+)\)/);
        var name = m ? m[1] : label;
        var available = self.isProductAvailable(c);
        var $btn = $(
          '<button type="button" class="jsc-np-swatch" data-id="' + c.id + '"' +
            (available ? '' : ' disabled aria-disabled="true"') +
            ' title="' + (available ? name : name + ' (Sold out)') + '">' +
            '<span class="jsc-np-swatch__chip" style="background-image:url(' + (c.thumb || c.image || '') + ')"></span>' +
          '</button>'
        );
        if (cover && cover.id === c.id) $btn.addClass('is-selected');
        if (!available) $btn.addClass('is-disabled is-sold-out');
        $sw.append($btn);
      });
      if (cover) {
        var nm = cover.title || '';
        var mm = nm.match(/\(([^)]+)\)/);
        var coverName = mm ? mm[1] : nm;
        if (!this.isProductAvailable(cover)) coverName += ' (Sold out)';
        this.$root.find('[data-np-cover-name]').text(coverName);
      }
    },

    insertThumbnail: function (insert) {
      if (!insert) return '';
      if (this.isInsertMediaMobile()) {
        return insert.thumbnailMobile || insert.thumbnailDesktop || insert.thumbnail || '';
      }
      return insert.thumbnailDesktop || insert.thumbnail || '';
    },

    insertFullImages: function (insert) {
      if (!insert) return [];
      if (this.isInsertMediaMobile()) {
        if (insert.imagesFullMobile && insert.imagesFullMobile.length) return insert.imagesFullMobile;
        if (insert.imagesFullDesktop && insert.imagesFullDesktop.length) return insert.imagesFullDesktop;
        return [insert.imageFullMobile || insert.imageFullDesktop].filter(Boolean);
      }
      if (insert.imagesFullDesktop && insert.imagesFullDesktop.length) return insert.imagesFullDesktop;
      return [insert.imageFullDesktop].filter(Boolean);
    },

    insertFullImage: function (insert) {
      return this.insertFullImages(insert)[0] || '';
    },

    insertViewerSources: function (insert) {
      var images = this.insertFullImages(insert);
      if (images.length) return images;
      var fallback = this.insertThumbnail(insert) || (insert && insert.product && insert.product.image) || '';
      return fallback ? [fallback] : [];
    },

    showInsertViewerImage: function (index) {
      var images = this.insertViewerImages || [];
      var $image = this.$root.find('[data-np-insert-viewer-image]');
      var hasMultiple = images.length > 1;
      this.$root
        .find('[data-np-insert-viewer-prev], [data-np-insert-viewer-next]')
        .prop('hidden', !hasMultiple);
      if (!images.length) {
        $image.attr('src', '').hide();
        return;
      }

      this.insertViewerImageIndex = (index + images.length) % images.length;
      var src = images[this.insertViewerImageIndex];
      if ($image.attr('src') !== src) {
        // Do not paint the next image at native size before its contain-fit
        // transform is recalculated by the existing load handler.
        $image.css('visibility', 'hidden').attr('src', src);
      } else if (this.insertViewerActive) {
        this.resetInsertViewerZoomFit();
      }
      $image.show();
    },

    changeInsertViewerImage: function (delta) {
      if ((this.insertViewerImages || []).length < 2) return;
      this.showInsertViewerImage((this.insertViewerImageIndex || 0) + delta);
    },

    renderInsert: function () {
      var $sel = this.$root.find('[data-np-insert-select]').empty();
      var self = this;
      $sel.append(
        $('<option></option>')
          .val('')
          .text('Select an option')
          .prop('disabled', true)
          .prop('selected', this.state.insertIndex == null)
      );
      (this.config.inserts || []).forEach(function (ins) {
        var title = ins.product ? ins.product.title : ('Insert ' + ins.index);
        var available = self.isProductAvailable(ins.product);
        var $opt = $('<option></option>').val(ins.index).text(available ? title : title + ' (Sold out)');
        if (!available) $opt.prop('disabled', true);
        $sel.append($opt);
      });
      if (this.state.insertIndex != null) {
        $sel.val(this.state.insertIndex);
      } else {
        $sel.prop('selectedIndex', 0);
      }
      this.$root.find('[data-np-insert-detail]').prop('hidden', !this.getInsert());
      var insert = this.getInsertForDetail();
      if (insert) {
        this.npInsertMediaMobile = this.isInsertMediaMobile();
        var thumb = this.insertThumbnail(insert) || (insert.product && insert.product.image) || '';
        var fullImage = this.insertFullImage(insert) || thumb;
        var insertTitle = (insert.product && insert.product.title) || 'Insert preview';
        this.$root.find('[data-np-insert-thumb]').attr('src', thumb || '').toggle(!!thumb);
        this.$root
          .find('[data-np-insert-viewer-image]')
          .attr('src', fullImage || '')
          .attr('alt', insertTitle)
          .toggle(!!fullImage);
        this.$root.find('[data-np-insert-viewer-open]').prop('disabled', !fullImage);
        this.$root.find('[data-np-insert-desc]').html(insert.descriptionHtml || '');
      }
      var quiz = this.config.quizUrl;
      var $quiz = this.$root.find('[data-np-quiz]');
      $quiz.removeAttr('target').removeAttr('rel');
      if (quiz) {
        $quiz.attr('href', quiz).removeAttr('aria-disabled');
      } else {
        $quiz.attr('href', '#').attr('aria-disabled', 'true').on('click.npquiz', function (e) {
          e.preventDefault();
        });
      }
    },

    renderAddons: function () {
      var $r = this.$root;
      var disabled = this.state.noAddOns;
      var charmsAvailable = this.isCharmsFeatureAvailable();
      if (!charmsAvailable) {
        this.state.addCharms = false;
        this.state.tpbCharmIds = [];
        this.state.regularCharmIds = [];
        this.state.charmOrderIds = [];
      }
      if (!this.hasAnyAvailableAddon()) {
        $r.find('[data-np-addons-choice-error]').prop('hidden', true);
      }
      $r.find('[data-np-no-addons]').prop('checked', this.state.noAddOns);
      $r.find('[data-np-add-charms]')
        .prop('checked', this.state.addCharms)
        .prop('disabled', !charmsAvailable || disabled);
      $r.find('[data-np-charms-sold-out]').prop('hidden', charmsAvailable);
      $r.find('[data-np-charms-block]')
        .toggleClass('is-sold-out', !charmsAvailable)
        .toggleClass('is-disabled', disabled || !charmsAvailable);

      var tpbLimit = Number(this.config.tpbCharms.limit) || 1;
      var regLimit = Number(this.config.regularCharms.limit) || 2;
      var parentAvailable = this.isCharmParentAvailable();
      var tpbGroupSoldOut =
        !parentAvailable ||
        !this.canFulfillCharmGroup(this.config.tpbCharms.products, tpbLimit);
      var regGroupSoldOut =
        !parentAvailable ||
        !this.canFulfillCharmGroup(this.config.regularCharms.products, regLimit);

      $r.find('[data-np-tpb-limit], [data-np-tpb-limit-dup]').text(tpbLimit);
      $r.find('[data-np-regular-limit], [data-np-regular-limit-dup]').text(regLimit);
      $r.find('[data-np-charms-set-count]').text(tpbLimit + regLimit);
      $r.find('[data-np-tpb-count]').text(this.state.tpbCharmIds.length);
      $r.find('[data-np-regular-count]').text(this.state.regularCharmIds.length);
      $r.find('[data-np-tpb-sold-out]').prop('hidden', !tpbGroupSoldOut);
      $r.find('[data-np-regular-sold-out]').prop('hidden', !regGroupSoldOut);

      var self = this;
      var tpbNames = [];
      var $tpb = $r.find('[data-np-tpb-charms]').empty();
      (this.config.tpbCharms.products || []).forEach(function (p, i) {
        var selected = self.state.tpbCharmIds.indexOf(p.id) > -1;
        if (selected) tpbNames.push(p.title);
        var available = self.isProductAvailable(p);
        var $c = $('<button type="button" class="jsc-np-charm" data-id="' + p.id + '" data-index="' + i + '"' +
          (available && charmsAvailable ? '' : ' disabled aria-disabled="true"') + '></button>');
        $c.append($('<img>').attr('src', p.thumb || p.image || '').attr('alt', p.title || ''));
        if (selected) $c.addClass('is-selected');
        if (disabled || !available || !charmsAvailable) $c.addClass('is-disabled');
        if (!available || !charmsAvailable) $c.addClass('is-sold-out');
        $tpb.append($c);
      });
      $r.find('[data-np-tpb-names]').text(tpbNames.length ? tpbNames.join(', ') : '');

      var regNames = [];
      var $reg = $r.find('[data-np-regular-charms]').empty();
      (this.config.regularCharms.products || []).forEach(function (p, i) {
        var selected = self.state.regularCharmIds.indexOf(p.id) > -1;
        if (selected) regNames.push(p.title);
        var available = self.isProductAvailable(p);
        var $c = $('<button type="button" class="jsc-np-charm" data-id="' + p.id + '" data-index="' + i + '"' +
          (available && charmsAvailable ? '' : ' disabled aria-disabled="true"') + '></button>');
        $c.append($('<img>').attr('src', p.thumb || p.image || '').attr('alt', p.title || ''));
        if (selected) $c.addClass('is-selected');
        if (disabled || !available || !charmsAvailable) $c.addClass('is-disabled');
        if (!available || !charmsAvailable) $c.addClass('is-sold-out');
        $reg.append($c);
      });
      $r.find('[data-np-regular-names]').text(regNames.length ? regNames.join(', ') : '');

      var bundlePct = this.pricing().bundlePct || 0;
      var bundleLabel = this.config.labels.bundleBadge || 'Bundle & Save';
      var $charmsPrice = $r.find('[data-np-charms-price]').empty();
      // Always show the Mini Charms set price when available (parent SKU), not only after select.
      if (!disabled && charmsAvailable) {
        var charmParent = this.charmParent();
        var charmsTotal =
          (charmParent && charmParent.price) || this.charmTotals().total || 0;
        if (charmsTotal) {
          if (this.state.addCharms && bundlePct > 0) {
            $charmsPrice.append(
              $('<span class="jsc-np-price-sale"></span>').text(
                money(discountedCents(charmsTotal, bundlePct), this.config)
              )
            );
            $charmsPrice.append(
              $('<span class="jsc-np-price-list is-compare"></span>').text(
                money(charmsTotal, this.config)
              )
            );
            $charmsPrice.append(
              $('<span class="jsc-np-price-discount"></span>').text(bundleLabel + ' ' + bundlePct + '% Off')
            );
          } else {
            $charmsPrice.append(
              $('<span class="jsc-np-price-list"></span>').text(money(charmsTotal, this.config))
            );
          }
        }
      }

      var $acc = $r.find('[data-np-accessories]').empty();
      $r.find('[data-np-accessories]').toggleClass('is-disabled', disabled);
      (this.config.accessories || []).forEach(function (p) {
        var checked = self.state.accessoryIds.indexOf(p.id) > -1;
        var available = self.isProductAvailable(p);
        var rowDisabled = disabled || !available;
        var priceHtml = '';
        if (checked && bundlePct > 0) {
          priceHtml =
            '<span class="jsc-np-price-sale">' +
            money(discountedCents(p.price, bundlePct), self.config) +
            '</span>' +
            '<span class="jsc-np-price-list is-compare">' +
            money(p.price, self.config) +
            '</span>' +
            '<span class="jsc-np-price-discount">' +
            bundleLabel +
            ' ' +
            bundlePct +
            '% Off</span>';
        } else {
          priceHtml =
            '<span class="jsc-np-price-list">' + money(p.price, self.config) + '</span>';
        }
        var $row = $(
          '<label class="jsc-np-accessory' + (rowDisabled ? ' is-disabled' : '') + (!available ? ' is-sold-out' : '') + '">' +
            '<img src="' + (p.thumb || p.image || '') + '" alt="">' +
            '<div class="jsc-np-accessory__info">' +
              '<span class="jsc-np-accessory__title">' + (p.title || '') + (!available ? ' (Sold out)' : '') + '</span>' +
              '<span class="jsc-np-accessory__price">' + priceHtml + '</span>' +
            '</div>' +
            '<input type="checkbox" data-np-accessory data-id="' + p.id + '"' + (checked ? ' checked' : '') + (rowDisabled ? ' disabled' : '') + '>' +
          '</label>'
        );
        if (p.shortDescription) {
          $row.find('.jsc-np-accessory__info').append(
            $('<p class="jsc-np-accessory__desc"></p>').text(p.shortDescription)
          );
        }
        $acc.append($row);
      });

      $r.find('[data-np-tpb-charms-wrap]')
        .toggleClass('is-sold-out', tpbGroupSoldOut)
        .toggleClass('is-disabled', disabled || !charmsAvailable || tpbGroupSoldOut);
      $r.find('[data-np-regular-charms-wrap]')
        .toggleClass('is-sold-out', regGroupSoldOut)
        .toggleClass('is-disabled', disabled || !charmsAvailable || regGroupSoldOut);
      this.setAddonsCollapsed(!!this.state.noAddOns);
    },

    renderMonogram: function () {
      var $r = this.$root;
      var monoAvailable = this.isMonogramProductAvailable();
      if (!monoAvailable) {
        this.state.wantMonogram = false;
        this.state.noMonogram = true;
        $r.find('[data-np-monogram-choice-error]').prop('hidden', true);
      }
      var active = this.state.wantMonogram && !this.state.noMonogram && monoAvailable;
      var fieldsVisible = !this.state.noMonogram && monoAvailable;
      $r.find('[data-np-no-monogram]')
        .prop('checked', !!this.state.noMonogram)
        .prop('disabled', !monoAvailable);
      $r.find('[data-np-want-monogram]').prop('checked', active).prop('disabled', !monoAvailable);
      $r.find('[data-np-monogram-fields]').toggle(fieldsVisible);
      $r.find('[data-np-tnc-wrap]').toggle(fieldsVisible);
      $r.find('[data-np-monogram-sold-out]').remove();
      if (!monoAvailable) {
        $r.find('[data-np-want-monogram]').closest('label').after(
          '<div class="jsc-np-monogram-sold-out" data-np-monogram-sold-out>Monogram is currently sold out</div>'
        );
      }

      var mono = this.config.monogram || {};
      var max = this.getMonogramCharLimit();
      this.state.monogramText = this.sanitizeMonogramText(this.state.monogramText);
      $r.find('[data-np-mono-max]').text(max);
      $r.find('[data-np-mono-count]').text((this.state.monogramText || '').length);
      $r.find('[data-np-mono-preview]').text(
        this.state.monogramText ? ': ' + this.state.monogramText.toUpperCase() : ''
      );
      $r.find('[data-np-mono-input]').attr('maxlength', max).val(this.state.monogramText || '');

      if (mono.product) {
        $r.find('[data-np-monogram-price]').text(money(mono.product.price, this.config));
      }

      var atMax = (this.state.monogramText || '').length >= max;
      var keyboardRows = ['ABCDEFGHI', 'JKLMNOPQR', 'STUVWXYZ'];
      keyboardRows.forEach(function (chars, i) {
        var $row = $r.find('[data-np-keyboard-row="' + (i + 1) + '"]').empty();
        chars.split('').forEach(function (ch) {
          var $btn = $('<button type="button" data-np-key="' + ch + '">' + ch + '</button>');
          if (atMax) $btn.prop('disabled', true).addClass('is-disabled');
          $row.append($btn);
        });
      });

      var $fonts = $r.find('[data-np-fonts]').empty();
      var self = this;
      (mono.fonts || []).forEach(function (f) {
        var $b = $(
          '<button type="button" class="jsc-np-option-btn" data-id="' +
            f.id +
            '">' +
            (f.fontText || f.label || 'Font') +
            '</button>'
        );
      
        if (Number(self.state.monogramFontId) === Number(f.id)) {
          $b.addClass('is-selected');
        }
      
        // $b[0].style.setProperty(
        //   'font-family',
        //   f.fontFamily || 'inherit',
        //   'important'
        // );
        // if (isSerifFontStyle(f.fontText)) {
        //   $b[0].style.setProperty('font-weight', '600', 'important');
        // }

        $fonts.append($b);
      });

      $r.find('[data-np-tnc]').prop('checked', this.state.monogramTerms);
    },

    overlayFor: function (desktopList, mobileList, index) {
      var list = this.isMobile() ? (mobileList || desktopList) : (desktopList || mobileList);
      if (!list || !list.length) return null;
      return list[index] || null;
    },

    renderPreview: function () {
      var $preview = $('[data-np-preview]');
      if (!$preview.length) {
        this.injectPreviewSlide();
        $preview = $('[data-np-preview]');
      }
      if (!$preview.length) return;

      var coverIdx = this.getCoverIndex();
      var coverSrc = this.overlayFor(this.config.coverOverlaysDesktop, this.config.coverOverlaysMobile, coverIdx);
      var coverMobileSrc = (this.config.coverOverlaysMobile || [])[coverIdx] || coverSrc;
      var cover = this.getCover();
      if (!coverSrc && cover) coverSrc = cover.image;
      $preview.find('[data-np-layer="cover"]').attr('src', coverSrc || '').prop('hidden', !coverSrc);
      this.syncPreviewAspectRatio($preview);
      $preview.find('[data-np-cover-mobile]').attr('srcset', coverMobileSrc || coverSrc || '');
      $('.jsc-np-preview-thumbnail img').attr('src', coverMobileSrc || coverSrc || '');
      this.syncPreviewZoomTarget(coverIdx, coverSrc);

      var insert = this.getInsert();
      var insertSrc = null;
      if (this.state.currentStep >= 1 || this.state.maxStepReached >= 1) {
        if (insert) {
          insertSrc = this.isMobile()
            ? (insert.overlayMobile || insert.overlayDesktop)
            : (insert.overlayDesktop || insert.overlayMobile);
        }
      }
      var $insert = $preview.find('[data-np-layer="insert"]');
      if (insertSrc) {
        $insert.attr('src', insertSrc).prop('hidden', false);
      } else {
        $insert.prop('hidden', true);
      }

      // Charms use charms_position slots. Accessories are full-bleed overlays (no custom position).
      var $charms = $preview.find('[data-np-layer="charms"]').empty();
      var positions = this.charmPositions();
      var self = this;
      if (!this.state.noAddOns) {
        var tpbProducts = this.config.tpbCharms.products || [];
        var regularProducts = this.config.regularCharms.products || [];
        this.orderedCharmIds().forEach(function (id, selectionIndex) {
          var isTpb = self.state.tpbCharmIds.indexOf(id) > -1;
          var products = isTpb ? tpbProducts : regularProducts;
          var listIndex = self.productListIndex(products, id);
          if (listIndex < 0) return;
          // Charm overlays use desktop assets on all breakpoints (no separate mobile field).
          var overlays = isTpb
            ? self.config.tpbCharms.overlaysDesktop
            : self.config.regularCharms.overlaysDesktop;
          var src = self.overlayFor(overlays, null, listIndex);
          if (!src) return;
          var pos = positions[selectionIndex] || {
            top: '10%',
            left: 10 + selectionIndex * 10 + '%'
          };
          $charms.append(
            $('<img class="jsc-np-preview__charm">').attr('src', src).css({ top: pos.top, left: pos.left })
          );
        });
      }

      var $acc = $preview.find('[data-np-layer="accessories"]').empty();
      if (!this.state.noAddOns) {
        var accessories = this.config.accessories || [];
        (this.state.accessoryIds || []).forEach(function (id) {
          var listIndex = self.productListIndex(accessories, id);
          if (listIndex < 0) return;
          var src = self.overlayFor(
            self.config.accessoryOverlaysDesktop,
            self.config.accessoryOverlaysMobile,
            listIndex
          );
          if (!src) return;
          $acc.append($('<img class="jsc-np-preview__accessory">').attr('src', src));
        });
      }

      var $mono = $preview.find('[data-np-layer="monogram"]');
      if (this.state.wantMonogram && !this.state.noMonogram && this.state.monogramText && this.isMonogramProductAvailable()) {
        var font = this.getMonogramFont();
        var pos = this.monogramPosition();
        var size = this.isMobile()
          ? (font && font.sizeMobile) || '18'
          : (font && font.sizeDesktop) || '24';
        $mono
          .text(this.state.monogramText.toUpperCase())
          .css({
            top: pos.top,
            left: pos.left,
            transform: 'translate(-50%, -30%)',
            fontFamily: (font && font.fontFamily) || 'serif',
            fontSize: size + (String(size).indexOf('px') > -1 ? '' : 'px')
          })
          .prop('hidden', false);
      } else {
        $mono.prop('hidden', true);
      }
    },

    renderPrice: function () {
      var p = this.pricing();
      var $r = this.$root;
      // Mirrors snippets/price.liquid: only the current price carries the code.
      $r.find('[data-np-price-final]').text(money(p.final, this.config, true));
      var $compare = $r.find('[data-np-price-compare]');
      if (p.final < p.subtotal) {
        $compare.text(money(p.subtotal, this.config)).show();
      } else {
        $compare.text('').hide();
      }
      var $badges = $r.find('[data-np-price-badges]').empty();
      if (p.earlyPct > 0) {
        $badges.append(
          $('<span class="jsc-np-badge"></span>').text(
            this.config.labels.earlyBirdBadge + ' ' + p.earlyPct + '% Off'
          )
        );
      }
      if (p.bundlePct > 0) {
        $badges.append(
          $('<span class="jsc-np-badge"></span>').text(
            this.config.labels.bundleBadge + ' ' + p.bundlePct + '% Off'
          )
        );
      }
      this.mountThemeAtc();
      $r.find('[data-np-atc-price]').text(money(p.final, this.config));
      this.syncThemeAtcState(this.canAddToCart());
      this.syncStickyAtc(p);
      this.syncAdditionalItemsToForm();
    },

    getStickyAtcContainer: function () {
      var $theme = $('.jsc-sticky-add-to-cart-container[theme]');
      if ($theme.length) return $theme;
      return $('.jsc-sticky-add-to-cart-container[pdp]');
    },

    // Keep the theme mobile sticky bar in sync with the configured NP total.
    syncStickyAtc: function (pricing) {
      if (!document.documentElement.classList.contains('jsc-np-page') && !document.body.classList.contains('jsc-np-page')) {
        return;
      }
      var $sticky = this.getStickyAtcContainer();
      if (!$sticky.length) return;

      var $price = $sticky.find('.jsc-sticky-product-price');
      var $info = $sticky.find('.jsc-sticky-product-info');
      var $compare = $sticky.find('.jsc-sticky-product-compare-price');
      var formattedFinal = money(pricing.final, this.config);
      $price.text(formattedFinal);

      if (pricing.final < pricing.subtotal) {
        $price.addClass('discounted-price');
        if (!$compare.length && $info.length) {
          $compare = $(
            '<div class="jsc-sticky-product-compare-price m-l-5 line-through-text-deco"></div>'
          );
          $info.append($compare);
        }
        $compare.text(money(pricing.subtotal, this.config)).show();
      } else {
        $price.removeClass('discounted-price');
        if ($compare.length) $compare.text('').hide();
      }

      var $btn = $sticky.find('.jsc-sticky-atc-btn');
      if (!$btn.length) return;
      var step = this.state.currentStep;

      // Mirror the in-step footer, which pairs Previous Step with the primary action.
      var $actions = $sticky.find('.jsc-np-sticky-actions');
      if (!$actions.length) {
        $actions = $('<div class="jsc-np-sticky-actions"></div>');
        $btn.before($actions);
        $actions.append(
          '<button type="button" class="btn btn--secondary jsc-np-sticky-prev" data-np-sticky-prev>PREVIOUS STEP</button>'
        );
        $actions.append($btn);
      }
      $actions.find('[data-np-sticky-prev]').toggle(step > 0);

      // The theme markup ships an inline onclick that submits the plain product form.
      $btn.removeAttr('onclick');
      if ($btn[0]) $btn[0].onclick = null;
      $btn.removeAttr('style');
      $btn.removeClass('btn--tertiary is-sold-out');

      var comingSoon = step === 3 && this.isComingSoon();
      var label = step < 3 ? 'NEXT STEP' : 'ADD TO CART';
      if (comingSoon) label = 'COMING SOON';
      var source = this.stepActionButton(step < 3 ? 'next' : 'atc');
      var enabled = source
        ? !(source.disabled || source.getAttribute('aria-disabled') === 'true')
        : step < 3
          ? this.canAdvanceFromStep(step)
          : this.canAddToCart();
      $btn.attr('data-np-sticky-action', step < 3 ? 'next' : 'atc');
      $btn.attr('data-available', enabled ? 'true' : 'false');
      $btn.prop('disabled', !enabled);
      $btn.attr('aria-disabled', enabled ? 'false' : 'true');
      if (comingSoon) {
        // Matches the mounted theme button: full-strength Add to Cart styling.
        $btn.css('cursor', 'not-allowed');
      } else if (!enabled) {
        $btn.addClass('btn--tertiary is-sold-out');
        $btn.css({ opacity: '0.6', cursor: 'not-allowed' });
      }
      $btn.text(label);
    },

    isComingSoon: function () {
      return !!(this.config && this.config.comingSoon);
    },

    canAddToCart: function () {
      if (this.isComingSoon()) return false;
      var insert = this.getInsert();
      return (
        this.isCurrentCoverAvailable() && !!(insert && this.isProductAvailable(insert.product))
      );
    },

    activeStepPanel: function () {
      return this.$root
        .find('[data-np-step-panel]')
        .filter(function () {
          return !this.hidden;
        })
        .first();
    },

    // The sticky is a remote control for the step footer, so reuse its buttons verbatim.
    stepActionButton: function (kind) {
      var $panel = this.activeStepPanel();
      if (!$panel.length) return null;
      if (kind === 'prev') return $panel.find('[data-np-prev]')[0] || null;
      if (kind === 'next') return $panel.find('[data-np-next]')[0] || null;
      this.mountThemeAtc();
      return (
        $panel.find('[data-np-atc-mount] button[name="add"], [data-np-atc-mount] button.add-to-cart')[0] ||
        this.getThemeAtcButton()
      );
    },

    handleStickyPrevClick: function () {
      var source = this.stepActionButton('prev');
      if (source) {
        source.click();
        return;
      }
      this.goToStep(Math.max(0, this.state.currentStep - 1));
    },

    handleStickyAtcClick: function (btn) {
      var action = (btn && btn.getAttribute('data-np-sticky-action')) || '';
      var isNext = action === 'next' || this.state.currentStep < 3;
      if (!isNext && this.isComingSoon()) return;
      var source = this.stepActionButton(isNext ? 'next' : 'atc');

      if (source) {
        source.click();
        return;
      }

      if (isNext) {
        this.handleNext();
        return;
      }

      this.mountThemeAtc();
      if (!this.validateMonogram()) return;

      var themeBtn = this.getThemeAtcButton();
      if (themeBtn) themeBtn.click();
    },

    getProductForm: function () {
      return document.querySelector('product-form.jsc-np-product-form .js-product-form') ||
        document.querySelector('product-form.js-product .js-product-form');
    },

    mountThemeAtc: function () {
      
      var mount = this.$root.find('[data-np-atc-mount]')[0];
      if (!mount) return;

      var buyButtons = document.querySelector('buy-buttons.main-prod-buy-buttons-row');
      if (!buyButtons) {
        if (!this._atcMountWarned) {
          this._atcMountWarned = true;
          console.error(
            '[NP] No "Buy buttons" block on this product template, so there is no Add to Cart button or product form to use.'
          );
        }
        return;
      }
      if (mount.contains(buyButtons)) return;

      buyButtons.classList.add('jsc-np-buy-buttons');
      mount.appendChild(buyButtons);

      var btn = this.getThemeAtcButton();
      // A coming soon button reads as a status, so it carries no configured total.
      if (btn && !this.isComingSoon() && !btn.querySelector('[data-np-atc-price]')) {
        var price = document.createElement('span');
        price.setAttribute('data-np-atc-price', '');
        price.className = 'jsc-np-atc-price';
        btn.appendChild(price);
      }
    },

    getThemeAtcButton: function () {
      var form = this.getProductForm();
      if (!form) return null;
      var host = form.closest('product-form') || form.parentElement;
      return host ? host.querySelector('button[name="add"], button.add-to-cart') : null;
    },

    syncThemeAtcState: function (canAtc) {
      var btn = this.getThemeAtcButton();
      if (!btn) return;
      // Liquid already rendered the disabled "Coming Soon" state; leave it alone.
      if (this.isComingSoon()) return;
      btn.disabled = !canAtc;
      btn.setAttribute('aria-disabled', canAtc ? 'false' : 'true');
      btn.classList.toggle('is-sold-out', !canAtc);
    },

    getFormId: function () {
      if (this._formId) return this._formId;
      var attr = this.$root.attr('data-np-form-id');
      if (attr) {
        this._formId = attr;
      } else {
        var form = this.getProductForm();
        this._formId = form ? form.id : '';
      }
      return this._formId;
    },

    appendHiddenInput: function (container, name, value, field, index) {
      if (!container) return;
      var input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value == null ? '' : String(value);
      var formId = this.getFormId();
      if (formId) input.setAttribute('form', formId);
      if (field) input.setAttribute('data-np-field', field);
      if (index) input.setAttribute('data-np-index', index);
      container.appendChild(input);
    },

    getItemsBucket: function (name) {
      var el = this.$root.find('[data-np-items-bucket="' + name + '"]')[0];
      return el || null;
    },

    clearItemsBuckets: function () {
      this.$root.find('[data-np-items-bucket]').each(function () {
        this.innerHTML = '';
      });
    },

    syncAdditionalItemsToForm: function () {
      var form = this.getProductForm();
      var cover = this.getCover();
      var insert = this.getInsert();
      if (!cover || !cover.variantId) return null;

      if (form) {
        var idInput = form.querySelector('[name="id"]');
        if (idInput) {
          idInput.disabled = false;
          idInput.value = String(cover.variantId);
        }
      }

      var bindId = 'np-' + cover.variantId + '-' + Date.now();
      this._pendingBindId = bindId;

      this.clearItemsBuckets();

      var mainBucket = this.getItemsBucket('main');
      this.appendHiddenInput(mainBucket, 'properties[_MainNotebook]', 'Yes', 'properties');
      this.appendHiddenInput(mainBucket, 'properties[_BindID]', bindId, 'properties');
      this.appendHiddenInput(mainBucket, 'properties[_NP Bundle]', 'Notebook Personalisation', 'properties');
      this.appendHiddenInput(mainBucket, 'properties[_NP Title]', this.config.title || cover.title || '', 'properties');
      this.appendHiddenInput(
        mainBucket,
        'properties[_Insert]',
        (insert && insert.product && insert.product.title) || (insert && insert.title) || '',
        'properties'
      );

      var parentId = String(cover.variantId);
      var index = 1;
      var self = this;
      var npBundle = 'Notebook Personalisation';

      function pushAdditional(bucketName, variantId, props) {
        if (!variantId) return;
        var bucket = self.getItemsBucket(bucketName);
        var prefix = 'shopify-additional-items[' + index + ']';
        self.appendHiddenInput(bucket, prefix + '[id]', variantId, 'id', index);
        self.appendHiddenInput(bucket, prefix + '[parent_id]', parentId, 'parent_id', index);
        self.appendHiddenInput(bucket, prefix + '[quantity]', '1', 'quantity', index);
        if (props) {
          Object.keys(props).forEach(function (key) {
            self.appendHiddenInput(
              bucket,
              prefix + '[properties][' + key + ']',
              props[key],
              'properties',
              index
            );
          });
        }
        index += 1;
      }

      if (insert && insert.product && this.isProductAvailable(insert.product)) {
        pushAdditional('insert', insert.product.variantId, {
          '_NotebookInsert': cover.title,
          '_BindID': bindId,
          '_NP Bundle': npBundle
        });
      }

      if (!this.state.noAddOns) {
        var charmParent = this.charmParent();
        if (
          this.state.addCharms &&
          charmParent &&
          this.isProductAvailable(charmParent)
        ) {
          pushAdditional('addons', charmParent.variantId, {
            '_NotebookCharmParent': 'Yes',
            '_BindID': bindId,
            '_NP Bundle': npBundle
          });
        }
        (this.config.tpbCharms.products || []).forEach(function (p) {
          if (self.state.tpbCharmIds.indexOf(p.id) > -1 && self.isProductAvailable(p)) {
            pushAdditional('addons', p.variantId, {
              '_NotebookCharm': 'TPB',
              '_BindID': bindId,
              '_NP Bundle': npBundle
            });
          }
        });
        (this.config.regularCharms.products || []).forEach(function (p) {
          if (self.state.regularCharmIds.indexOf(p.id) > -1 && self.isProductAvailable(p)) {
            pushAdditional('addons', p.variantId, {
              '_NotebookCharm': 'Regular',
              '_BindID': bindId,
              '_NP Bundle': npBundle
            });
          }
        });
        (this.config.accessories || []).forEach(function (p) {
          if (self.state.accessoryIds.indexOf(p.id) > -1 && self.isProductAvailable(p)) {
            pushAdditional('addons', p.variantId, {
              '_NotebookAccessory': 'Yes',
              '_BindID': bindId,
              '_NP Bundle': npBundle
            });
          }
        });
      }

      if (this.state.wantMonogram && !this.state.noMonogram && this.isMonogramProductAvailable()) {
        var font = this.getMonogramFont();
        var fontLabel = (font && (font.label || font.fontFamily)) || '';
        var fontText = (font && font.fontText) || '';
        // Underscore keys stay hidden and drive cart grouping; the rest are shown to the customer.
        var monogramProps = {
          '_Personalise': cover.title,
          '_NotebookMonogram': 'Yes',
          '_BindID': bindId,
          '_NP Bundle': npBundle,
          'Personalisation': (this.state.monogramText || '').toUpperCase(),
          // 'Font Type': fontLabel,
          'Font': fontText
        };
        // var positionLabel = this.monogramPosition().label;
        // if (positionLabel) monogramProps['Position'] = positionLabel;
        pushAdditional('monogram', this.config.monogram.product.variantId, monogramProps);
      }

      this.verifyFormAssociation(form);

      return form;
    },

    // Elements outside the form rely on the form attribute; re-home them if association failed.
    verifyFormAssociation: function (form) {
      if (!form) return;
      var inputs = this.$root.find('[data-np-items-bucket] input').toArray();
      if (!inputs.length) return;
      if (inputs[0].form === form) return;

      var fallback = form.querySelector('[data-np-form-fallback]');
      if (!fallback) {
        fallback = document.createElement('div');
        fallback.setAttribute('data-np-form-fallback', '');
        fallback.hidden = true;
        form.appendChild(fallback);
      }
      fallback.innerHTML = '';
      inputs.forEach(function (input) {
        fallback.appendChild(input.cloneNode(true));
      });
    },

    buildItemsFromProductForm: function (form) {
      var idInput = form.querySelector('[name="id"]');
      var parentId = idInput ? Number(idInput.value) : null;
      if (!parentId) return null;

      var qtyInput = form.querySelector('[name="quantity"]');
      var qty = qtyInput ? Number(qtyInput.value) || 1 : 1;

      var mainProps = {};
      this.$root.find('[data-np-items-bucket] input[name^="properties["]').each(function () {
        var match = this.name.match(/^properties\[(.+)\]$/);
        if (match) mainProps[match[1]] = this.value;
      });

      var items = [{ id: parentId, quantity: qty, properties: mainProps }];
      var grouped = {};

      this.$root.find('[data-np-items-bucket] input').each(function () {
        var input = this;
        var propMatch = input.name.match(/^shopify-additional-items\[(\d+)\]\[properties\]\[(.+)\]$/);
        if (propMatch) {
          var propIdx = propMatch[1];
          if (!grouped[propIdx]) grouped[propIdx] = { properties: {} };
          grouped[propIdx].properties[propMatch[2]] = input.value;
          return;
        }

        var fieldMatch = input.name.match(/^shopify-additional-items\[(\d+)\]\[([^\[\]]+)\]$/);
        if (!fieldMatch) return;
        var idx = fieldMatch[1];
        if (!grouped[idx]) grouped[idx] = { properties: {} };
        grouped[idx][fieldMatch[2]] = input.value;
      });

      Object.keys(grouped)
        .sort(function (a, b) { return Number(a) - Number(b); })
        .forEach(function (idx) {
          var row = grouped[idx];
          if (!row.id) return;
          var item = {
            id: Number(row.id),
            quantity: Number(row.quantity) || 1
          };
          if (row.parent_id) item.parent_id = Number(row.parent_id);
          if (row.properties && Object.keys(row.properties).length) {
            item.properties = row.properties;
          }
          items.push(item);
        });

      return items;
    },

    setAtcError: function (error) {
      var el = this.$root.find('[data-np-atc-error]')[0];
      if (!el) return;
      el.hidden = !error;
      el.innerHTML = '';
      if (!error) return;
      var messages = Array.isArray(error) ? error : [error];
      messages.forEach(function (msg, index) {
        if (index > 0) el.insertAdjacentHTML('beforeend', '<br>');
        el.insertAdjacentText('beforeend', String(msg));
      });
    },

    handleProductFormSubmit: function (evt) {
      var form = evt.target;
      if (!form || form !== this.getProductForm()) return;

      this.setAtcError(false);
      this.sanitizeUnavailableSelections();
      var cover = this.getCover();
      var insert = this.getInsert();
      var canAtc = this.isProductAvailable(cover) && !!(insert && this.isProductAvailable(insert.product));
      if (!canAtc) {
        evt.preventDefault();
        evt.stopImmediatePropagation();
        this.renderCover();
        this.renderInsert();
        this.renderPrice();
        return;
      }

      if (!this.validateAddons(true)) {
        evt.preventDefault();
        evt.stopImmediatePropagation();
        this.goToStep(2);
        var app = this;
        window.requestAnimationFrame(function () {
          var $error = app.$root
            .find(
              '[data-np-addons-choice-error]:not([hidden]),[data-np-charms-error]:not([hidden])'
            )
            .first();
          app.scrollToError($error);
        });
        return;
      }

      if (!this.validateMonogram()) {
        evt.preventDefault();
        evt.stopImmediatePropagation();
        return;
      }

      this.syncAdditionalItemsToForm();
      var items = this.buildItemsFromProductForm(form);
      if (!items || !items.length) return;

      // Ajax FormData often ignores shopify-additional-items; convert to JSON parent_id payload.
      evt.preventDefault();
      evt.stopImmediatePropagation();

      var self = this;
      var themeBtn = this.getThemeAtcButton();

      if (themeBtn) {
        themeBtn.setAttribute('aria-disabled', 'true');
        themeBtn.classList.add('is-loading');
      }

      var cartAddUrl =
        (window.theme && theme.routes && theme.routes.cartAdd) ||
        ((window.Shopify && Shopify.routes && Shopify.routes.root ? Shopify.routes.root : '/') + 'cart/add.js');

      fetch(cartAddUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({ items: items })
      })
        .then(function (res) {
          return res.json().then(function (data) {
            if (!res.ok) {
              var message =
                typeof data.description === 'string'
                  ? data.description
                  : data.message || 'ATC failed';
              if (data.errors && typeof data.errors === 'object') {
                message = Object.keys(data.errors).map(function (key) {
                  var val = data.errors[key];
                  return Array.isArray(val) ? val.join(', ') : String(val);
                });
              }
              var err = new Error(Array.isArray(message) ? message.join(', ') : message);
              err.payload = data;
              err.displayMessage = message;
              throw err;
            }
            return data;
          });
        })
        .then(function () {
          self.setAtcError(false);
          document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true }));
          document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', { bubbles: true }));
          document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));
          document.dispatchEvent(new CustomEvent('on:cart:add', { bubbles: true }));
          self.syncCartCounts();
          self.clearParams();
          setTimeout(function () {
            if (themeBtn) {
              themeBtn.removeAttribute('aria-disabled');
              themeBtn.classList.remove('is-loading');
              themeBtn.classList.add('is-success');
              setTimeout(function () {
                themeBtn.classList.remove('is-success');
              }, 2000);
            }
            self.renderPrice();
          }, 400);
        })
        .catch(function (err) {
          var variantIds = items.map(function (item) { return item.id; });
          console.error(
            '[NP] ATC error',
            err && err.payload ? err.payload : err,
            'variantIds:',
            variantIds
          );
          var display = (err && err.displayMessage) || (err && err.message) || 'Unable to add to cart.';
          self.setAtcError(display);
          document.dispatchEvent(
            new CustomEvent('on:cart:error', {
              bubbles: true,
              detail: {
                error: Array.isArray(display) ? display.join(', ') : String(display)
              }
            })
          );
          if (themeBtn) {
            themeBtn.removeAttribute('aria-disabled');
            themeBtn.classList.remove('is-loading');
          }
        });
    },

    addToCart: function () {
      if (this.isComingSoon()) return;
      var form = this.getProductForm();
      if (!form) return;
      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit();
      } else {
        $(form).trigger('submit');
      }
    },

    // Header listens for on:cart:change; drawer count is outside merge-list nodes
    // so also patch it from /cart.js (same pattern as jsc-resets-scripts).
    syncCartCounts: function () {
      var root =
        (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      fetch(root + 'cart.js', { credentials: 'same-origin' })
        .then(function (res) {
          if (!res.ok) throw new Error('cart.js ' + res.status);
          return res.json();
        })
        .then(function (cart) {
          var count = cart.item_count || 0;
          var label = count === 1 ? 'Item' : 'Items';
          document.querySelectorAll('.cart-link__count').forEach(function (el) {
            el.textContent = String(count);
          });
          document.querySelectorAll('.jsc-drawer-item-count').forEach(function (el) {
            el.innerHTML =
              '<span class="jsc-item-count">' + count + '</span> ' + label;
          });
        })
        .catch(function () {});
    }
  };

  NP.NotebookPersonalisation.init = function () {
    $('[data-np-root]').each(function () {
      var instance = Object.create(App);
      instance.init($(this));
    });
  };

  $(function () {
    NP.NotebookPersonalisation.init();
  });
})(window, window.jQuery);
